import { DeleteOutlined, InfoCircleOutlined, PlusOutlined, WarningFilled } from '@ant-design/icons';
import Cascader from 'antd/lib/cascader';
import Select from 'antd/lib/select';
import TextArea from 'antd/lib/input/TextArea';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { selectAppStoreIntegration, selectBetaTestingIntegration } from '../selectors/selectors';
import { TeamsChannelOverrideSelect } from '../components/sharedComponents/TeamsChannelOverrideSelect';
import CustomCITriggerHeadersEditor, {
    initialTriggerHeaderRows,
} from '@/components/integrations/CustomCITriggerHeadersEditor';
import SupportedTokensComponent from '@/components/common/SupportedTokensComponent';
import {
    commsChannelsValidatorFunction,
    getIsGooglePlayStore,
    testRunNamingKeywordsValidatorFunction,
    testRunNamePatternTokens,
    ticketCustomFieldsValidatorFunction,
} from '@/helpers';
import {
    AnalyticsProperty,
    AppPlatform,
    AppStoreType,
    AsanaProject,
    ASCAppInfo,
    ASCProductAndWorkflows,
    ASCWorkflow,
    AtlassianRotationInfo,
    AtlassianScheduleInfo,
    AzureBoardColumn,
    AzureProjectAndResources,
    AzureRepo,
    AzureWorkArea,
    BaseCIIntegrationData,
    BitbucketWorkspaceAndRepos,
    BitriseAppData,
    CustomCIIntegrationData,
    BugsnagOrgAndProjects,
    BuildkiteOrgAndPipelines,
    CodemagicAppData,
    CommsChannel,
    CommsChannelAvailableTokens,
    DatadogCustomMetric,
    DatadogMetricQueryAggregation,
    DynatraceRumApplication,
    EmbraceApp,
    EventAndParam,
    FirebaseAndroidApp,
    FirebaseIosApp,
    FirebaseProjectResources,
    GitLabProject,
    GoogleAnalyticsDataStream,
    GoogleAnalyticsResources,
    Integration,
    IntegrationDatadogAnalytics,
    IntegrationDatadogStabilityMonitoring,
    IntegrationMicrosoftTeams,
    IntegrationProvider,
    IntegrationProviderAmplitude,
    IntegrationProviderApple,
    IntegrationProviderAsana,
    IntegrationProviderAzure,
    IntegrationProviderAzureActiveDirectory,
    IntegrationProviderBitbucket,
    IntegrationProviderBitrise,
    IntegrationProviderBrowserStack,
    IntegrationProviderBugsnag,
    IntegrationProviderBuildkite,
    IntegrationProviderCircleCI,
    IntegrationProviderCodemagic,
    IntegrationProviderCrowdin,
    IntegrationProviderDatadog,
    IntegrationProviderDynatrace,
    IntegrationProviderEmbrace,
    IntegrationProviderLuciq,
    LuciqAnalyticsIntegrationData,
    LuciqStabilityMonitoringIntegrationData,
    IntegrationProviderExpoEAS,
    IntegrationProviderGeneric,
    IntegrationProviderGitHub,
    IntegrationProviderGitLab,
    IntegrationProviderGoogle,
    IntegrationProviderGoogleOAuth,
    IntegrationProviderIncidentIo,
    IntegrationProviderJenkins,
    IntegrationProviderJira,
    IntegrationProviderJiraServiceManagement,
    IntegrationProviderLaunchDarkly,
    IntegrationProviderLinear,
    IntegrationProviderLokalise,
    IntegrationProviderMixpanel,
    IntegrationProviderMonday,
    IntegrationProviderNewRelic,
    IntegrationProviderOpsgenie,
    IntegrationProviderOptimizely,
    IntegrationProviderPagerDuty,
    IntegrationProviderPivotal,
    IntegrationProviderQase,
    IntegrationProviderRootly,
    IntegrationProviderRunwayBuildDistro,
    IntegrationProviderSamsung,
    IntegrationProviderSentry,
    IntegrationProviderShortcut,
    IntegrationProviderSlack,
    IntegrationProviderStatsig,
    IntegrationProviderTestRail,
    ExpoEASProject,
    ExpoEASChannel,
    IntegrationScheduleInfo,
    IntegrationSlack,
    IntegrationType,
    JenkinsIntegrationData,
    JiraProject,
    LaunchDarklyEnvironment,
    LaunchDarklyProject,
    LinearTeam,
    MicrosoftTeamsChannel,
    MondayBoard,
    NewRelicMobileApplication,
    OBAAFilterOperator,
    OptimizelyEnvironment,
    OptimizelyProject,
    PatternToken,
    PivotalProject,
    ReleaseStepType,
    RunwayBuildDistroBucketInfo,
    SamsungApp,
    SentryOrgAndProjects,
    ShortcutGroup,
    ShortcutWorkflowState,
    StatsigEnvironment,
    WorkflowArgument,
    WorkflowEnabledSteps,
    WorkflowType,
} from '@/types';
import { datadogCustomMetricKey } from './datadogCustomMetrics';
import {
    datadogRumApplicationDisplayableString,
    FieldType,
    filterOptionLabel,
    FormFieldData,
    isValidDatadogRumApplicationTypeForAppPlatform,
} from './formHelpers';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Form, { Rule, RuleObject } from 'antd/lib/form';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '@/components/styles/globalStyles';
import AutoComplete from 'antd/lib/auto-complete';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Input from 'antd/lib/input';
import Tag from 'antd/lib/tag';
import Tabs from 'antd/lib/tabs';
import Tooltip from 'antd/lib/tooltip';
import { NamePath } from 'antd/lib/form/interface';
import { remove } from 'lodash';
import { eventPropertyTerminology } from './displayHelpers';
import Link from 'antd/lib/typography/Link';
import Popover from 'antd/lib/popover';
import { PatternTokenHelperTextComponent } from '@/components/appSettings/PatternTokenHelperTextComponent';

const FEATURE_FLAG_PLATFORM_FILTER_SUGGESTIONS: string[] = [
    'iOS',
    'Android',
    'Web',
    'macOS',
    'Windows',
    'tvOS',
    'watchOS',
    'Linux',
];
const STATSIG_PLATFORM_FILTER_SOURCE_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'rules', label: 'OS rule' },
    { value: 'name', label: 'Flag name' },
];
const STATSIG_PLATFORM_FILTER_MATCH_TYPE_OPTIONS = [
    { value: 'contains', label: 'contains' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'equals', label: 'equals' },
];
const STATSIG_RULES_MATCH_TYPE_OPTIONS = [{ value: 'equals', label: 'equals' }];

const getDefaultStatsigFilterSource = (
    integrationData: Integration['data'] & {
        selectedFeatureFlagPlatformFilterSource?: string;
        selectedFeatureFlagPlatformFilter?: string;
    },
): string => {
    const explicitSource = integrationData?.selectedFeatureFlagPlatformFilterSource;
    if (explicitSource && explicitSource.trim().length > 0) {
        return explicitSource;
    }
    if (integrationData?.selectedFeatureFlagPlatformFilter?.trim()) {
        // Backward compatibility for older saved configs that only persisted filter value.
        return 'rules';
    }
    return 'none';
};

function featureFlagPlatformFilterChipsFromStored(stored: string | undefined | null): string[] {
    if (stored == null || !String(stored).trim()) {
        return [];
    }
    return String(stored)
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

function featureFlagPlatformFilterTagsSelect(placeholder: string): React.ReactNode {
    return (
        <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder={placeholder}
            allowClear
            showSearch
            filterOption={filterOptionLabel}
            notFoundContent={null}
        >
            {FEATURE_FLAG_PLATFORM_FILTER_SUGGESTIONS.map((os) => (
                <Select.Option key={os} value={os} label={os}>
                    {os}
                </Select.Option>
            ))}
        </Select>
    );
}

export const isRequiredRule = (rule: RuleObject) => {
    return rule.required === true;
};

const testRunNamePatternField: (pattern: string | undefined) => FormFieldData = (pattern) => ({
    label: 'Test run naming pattern',
    tooltip:
        'Optionally narrow which test runs are associated with a release, by naming a pattern their name has to contain. Matched anywhere in the name.',
    helpMessage: <SupportedTokensComponent availableTokens={testRunNamePatternTokens} secondary />,
    name: 'testRunNamePattern',
    initialValue: pattern ?? '',
    fieldType: FieldType.Custom,
    rules: [{ required: false }],
    renderField: () => <Input allowClear placeholder="e.g. _{testedStoreBuildNumber}" />,
});

const prodWorkflowHelperMessageForPlatform = (platform: AppPlatform, workflowNoun?: string) => {
    switch (platform) {
        case AppPlatform.ReactNativeOTA:
            return 'The ' + (workflowNoun ?? 'workflow') + ' you use to deploy your update to production.';
        default:
            return (
                'Leave blank unless you generate your final release build using a different ' +
                (workflowNoun ?? 'workflow') +
                ' than your RC.'
            );
    }
};

const devWorkflowHelperMessage = (workflowNoun?: string) => {
    return (
        'Leave blank unless you generate working branch builds using a different ' +
        (workflowNoun ?? 'workflow') +
        ' than your release candidate builds.'
    );
};

export const updateIntegrationFormDataForIntegration: (
    integration: Integration,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    appId: string,
    appName: string,
    workflowEnabledSteps: WorkflowEnabledSteps,
    integrationProvider?: IntegrationProvider,
    onChangeBundleId?: (value) => void,
    bundleId?: string,
    onChangeAzureProjectId?: (value) => void,
    azureProjectId?: string,
    onChangeOptimizelyProjectId?: (value) => void,
    optimizelyProjectId?: string,
    onChangeLaunchDarklyProjectId?: (value) => void,
    launchDarklyProjectId?: string,
    onChangeFirebaseProjectId?: (value) => void,
    firebaseProjectId?: string,
    onChangeOpsgenieSchedule?: (value) => void,
    opsgenieSchedule?: string,
    onChangeJSMSchedule?: (value) => void,
    jsmSchedule?: string,
    autoSelectAllJiraProjects?: boolean,
    bitriseIsPipeline?: boolean,
    bitriseIsPipelineDev?: boolean,
    bitriseIsPipelineProd?: boolean,
) => FormFieldData[] = (
    integration,
    platform,
    appStoreType,
    appId,
    appName,
    workflowEnabledSteps,
    integrationProvider,
    onChangeBundleId,
    bundleId,
    onChangeAzureProjectId,
    azureProjectId,
    onChangeOptimizelyProjectId,
    optimizelyProjectId,
    onChangeLaunchDarklyProjectId,
    launchDarklyProjectId,
    onChangeFirebaseProjectId,
    firebaseProjectId,
    onChangeOpsgenieSchedule,
    opsgenieSchedule,
    onChangeJSMSchedule,
    jsmSchedule,
    autoSelectAllJiraProjects,
    bitriseIsPipeline,
    bitriseIsPipelineDev,
    bitriseIsPipelineProd,
) => {
    if (!integration) {
        return [];
    }

    const prodWorkflowLabel = 'Release workflow';
    const prodWorkflowHelperMessage = prodWorkflowHelperMessageForPlatform(platform);
    const rcBuildName = platform === AppPlatform.ReactNativeOTA ? 'Staging' : 'Release Candidate';
    const devWorkflowHelper = devWorkflowHelperMessage();
    const isWorkflowCIStepEnabled = workflowEnabledSteps[ReleaseStepType.ReleaseCandidate];

    const codemagicCascaderOptions = (apps: CodemagicAppData[]) => {
        return apps.map((app) => {
            const workflowKeys = Object.keys(app.workflows);
            const isDisabled = !app.workflows || workflowKeys.length === 0;
            return {
                value: app.id,
                label: app.appName,
                disabled: isDisabled,
                children: workflowKeys.map((workflowId) => {
                    return {
                        value: workflowId,
                        label: app.workflows[workflowId].name,
                        children: null,
                    };
                }),
            };
        });
    };

    const bitriseCascaderOptions = (apps: BitriseAppData[]) => {
        return apps.map((app) => {
            return {
                value: app.slug,
                label: app.title,
                disabled: !app.workflows || app.workflows?.length === 0,
                children:
                    app.workflows?.map((workflow) => {
                        return {
                            value: workflow,
                            label: workflow,
                            children: null,
                        };
                    }) ?? null,
            };
        });
    };

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const multiAPKField = (data?: BaseCIIntegrationData) => {
        return {
            helpMessage:
                'Select if you build and upload multiple APKs or AABs to Google Play Console from a single workflow.',
            initialValue: !!data?.allowMatchingMultipleAppStoreBuilds,
            name: 'allowMatchingMultipleAppStoreBuilds',
            fieldType: FieldType.Checkbox,
            rules: [{ required: true }],
            data: 'Multiple APK workflow',
        };
    };

    const workflowArgumentFormListName = (workflowType: WorkflowType) => `${workflowType}WorkflowArguments`;

    const formHasAnyWorkflowArgumentRows = (
        getFieldValue: (name: NamePath) => unknown,
        workflowTypes: readonly WorkflowType[],
    ): boolean => {
        for (const wt of workflowTypes) {
            const list = getFieldValue(workflowArgumentFormListName(wt));
            if (Array.isArray(list) && list.length > 0) {
                return true;
            }
        }
        return false;
    };

    /** Checkbox bound to isBuildVariablesFilteringStrict; visibility follows live Form.List rows (not only saved integration data). */
    const strictWorkflowArgumentFilteringFields = (
        data: BaseCIIntegrationData | undefined,
        isWorkflowCIStepEnabled: boolean,
    ): FormFieldData[] => {
        const workflowTypes =
            isWorkflowCIStepEnabled ?
                ([WorkflowType.Staging] as const)
            :   ([WorkflowType.Staging, WorkflowType.Working, WorkflowType.Production] as const);

        const helpWhenVisible =
            'If selected, Runway will only consider workflow runs that have all arguments present and with matching values. Otherwise, workflow runs that may be missing certain arguments are still included.';

        const strictFieldVisibleStyle: React.CSSProperties =
            isWorkflowCIStepEnabled ?
                { marginTop: 12, paddingLeft: 20 } // align with single workflow-args block
            :   { marginTop: 12 }; // legacy RC + dev + prod: align with other fields, not indented with args lists

        return [
            {
                name: 'isBuildVariablesFilteringStrict',
                fieldType: FieldType.Custom,
                skipFormItemWrapper: true,
                initialValue: !!data?.isBuildVariablesFilteringStrict,
                renderField: () => (
                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                            const visible = formHasAnyWorkflowArgumentRows(getFieldValue, workflowTypes);
                            return (
                                <Form.Item
                                    name="isBuildVariablesFilteringStrict"
                                    label={null}
                                    valuePropName="checked"
                                    extra={visible ? helpWhenVisible : undefined}
                                    style={visible ? strictFieldVisibleStyle : { marginTop: 12, display: 'none' }}
                                    colon={false}
                                >
                                    <Checkbox>Strict workflow argument filtering</Checkbox>
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                ),
            },
        ];
    };

    const workflowArguments = (integration.data as BaseCIIntegrationData)?.buildVariables ?? null;

    let calendarIntegrationAppDisplayName = appName;

    switch (integration.id) {
        case IntegrationType.GitHubVCSDistribution:
        case IntegrationType.GitHubVCS: {
            const provider = integrationProvider as IntegrationProviderGitHub;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: 'Repository name',
                    data: provider.data?.availableRepos,
                    initialValue: integration.data?.repoName,
                    name: 'repoName',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (repos: string[]) => {
                        return (
                            <Select showSearch allowClear filterOption={filterOptionLabel}>
                                {repos.map((repo) => (
                                    <Select.Option key={repo} value={repo} label={repo}>
                                        {repo}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.GitHubCI: {
            const provider = integrationProvider as IntegrationProviderGitHub;

            if (!provider) {
                return [];
            }

            function filter(inputValue, path) {
                return path.some((option) => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
            }

            const fields: FormFieldData[] = [
                {
                    label:
                        isWorkflowCIStepEnabled ? `Repository name and workflow` : (
                            `Repository name and ${rcBuildName} workflow`
                        ),
                    data: provider.data?.availableRepos?.map((repo) => {
                        const children = provider.data?.availableWorkflows[repo] ?? [];

                        return {
                            value: repo,
                            label: repo,
                            disabled: children.length === 0,
                            children: children.map((workflow) => {
                                return {
                                    value: workflow.id,
                                    label: workflow.name,
                                };
                            }),
                        };
                    }),
                    initialValue:
                        integration.data?.workflowId ?
                            [integration.data?.repoName, integration.data?.workflowId]
                        :   null,
                    name: 'repoNameAndWorkflow',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (data, extraProperties) => {
                        return (
                            <Cascader
                                options={data}
                                allowClear
                                showSearch={{ filter }}
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Staging),
            ];

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...[
                        {
                            label: `Working branch workflow`,
                            data: provider.data?.availableRepos?.map((repo) => {
                                const children = provider.data?.availableWorkflows[repo] ?? [];

                                return {
                                    value: repo,
                                    label: repo,
                                    disabled: children.length === 0,
                                    children: children.map((workflow) => {
                                        return {
                                            value: workflow.id,
                                            label: workflow.name,
                                        };
                                    }),
                                };
                            }),
                            initialValue:
                                integration.data?.devWorkflowId ?
                                    [integration.data?.repoName, integration.data?.devWorkflowId]
                                :   null,
                            name: 'repoNameAndDevWorkflow',
                            fieldType: FieldType.Custom,
                            helpMessage: devWorkflowHelper,
                            rules: [
                                { required: false },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (value) {
                                            const repo = value[0];
                                            const parentRepo = getFieldValue('repoNameAndWorkflow')?.[0];

                                            if (repo && parentRepo && repo !== parentRepo) {
                                                return Promise.reject(`Repo must equal ${rcBuildName} repo`);
                                            }
                                        }

                                        return Promise.resolve();
                                    },
                                }),
                            ],
                            extraFormItemProps: {
                                dependencies: ['repoNameAndWorkflow'],
                            },

                            renderField: (data, extraProperties) => {
                                return (
                                    <Cascader
                                        options={data}
                                        allowClear
                                        showSearch={{ filter }}
                                        placeholder={'Select'}
                                        {...extraProperties}
                                    />
                                );
                            },
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Working),
                        {
                            label: prodWorkflowLabel,
                            helpMessage: prodWorkflowHelperMessage,
                            data: provider.data?.availableRepos?.map((repo) => {
                                const children = provider.data?.availableWorkflows[repo] ?? [];

                                return {
                                    value: repo,
                                    label: repo,
                                    disabled: children.length === 0,
                                    children: children.map((workflow) => {
                                        return {
                                            value: workflow.id,
                                            label: workflow.name,
                                        };
                                    }),
                                };
                            }),
                            initialValue:
                                integration.data?.prodWorkflowId ?
                                    [integration.data?.repoName, integration.data?.prodWorkflowId]
                                :   null,
                            name: 'repoNameAndProdWorkflow',
                            rules: [
                                { required: false },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (value) {
                                            const repo = value[0];
                                            const parentRepo = getFieldValue('repoNameAndWorkflow')?.[0];

                                            if (repo && parentRepo && repo !== parentRepo) {
                                                return Promise.reject(`Repo must equal ${rcBuildName} repo`);
                                            }
                                        }

                                        return Promise.resolve();
                                    },
                                }),
                            ],
                            extraFormItemProps: {
                                dependencies: ['repoNameAndWorkflow'],
                            },
                            fieldType: FieldType.Custom,

                            renderField: (data, extraProperties) => {
                                return (
                                    <Cascader
                                        options={data}
                                        allowClear
                                        showSearch={{ filter }}
                                        placeholder={'Select'}
                                        {...extraProperties}
                                    />
                                );
                            },
                            placeholder: 'Select',
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Production),
                    ],
                );
            }

            fields.push(
                ...strictWorkflowArgumentFilteringFields(
                    integration.data as BaseCIIntegrationData,
                    isWorkflowCIStepEnabled,
                ),
            );
            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.GitHubIT: {
            const provider = integrationProvider as IntegrationProviderGitHub;

            if (!provider) {
                return [];
            }

            const hasExistingDoneStates = provider.data.issuesDoneStates.length > 0;

            return [
                {
                    label: 'Repository name',
                    data: provider.data?.availableRepos,
                    initialValue: integration.data?.repoName,
                    name: 'repoName',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (repos: string[]) => {
                        return (
                            <Select showSearch allowClear filterOption={filterOptionLabel}>
                                {repos.map((repo) => (
                                    <Select.Option key={repo} value={repo} label={repo}>
                                        {repo}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Additional repositories',
                    data: provider.data?.availableRepos,
                    initialValue: integration.data?.additionalRepoNames,
                    name: 'additionalRepoNames',
                    fieldType: FieldType.Custom,
                    rules: [
                        { required: false },
                        ({ getFieldValue }) => ({
                            validator(_, value: string[] | undefined) {
                                const primaryRepo = getFieldValue('repoName') as string | undefined;
                                if (
                                    primaryRepo &&
                                    (value ?? []).some((repo) => repo.toLowerCase() === primaryRepo.toLowerCase())
                                ) {
                                    return Promise.reject(
                                        'Additional repositories cannot include the primary repository',
                                    );
                                }

                                return Promise.resolve();
                            },
                        }),
                    ],
                    extraFormItemProps: {
                        dependencies: ['repoName'],
                    },
                    helpMessage:
                        'Optionally surface issues from more repos. Any issues-related automations you enable will also act on these additional issues.',

                    renderField: (repos: string[]) => {
                        return (
                            <Select showSearch allowClear mode="multiple" filterOption={filterOptionLabel}>
                                {repos.map((repo) => (
                                    <Select.Option key={repo} value={repo} label={repo}>
                                        {repo}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Additional done states',
                    initialValue: integration.data.additionalDoneStates,
                    data: provider.data.issuesAvailableAdditionalDoneStates,
                    name: 'additionalDoneStatuses',
                    fieldType: FieldType.Custom,
                    rules: [{ required: !hasExistingDoneStates }],
                    helpMessage: 'Existing GitHub-configured done states: '.concat(
                        hasExistingDoneStates ? provider.data.issuesDoneStates.join(', ') : '(none)',
                    ),

                    renderField: (availableAdditionalDoneStates: string[]) => {
                        return (
                            <Select allowClear mode="multiple" placeholder="Select additional done states">
                                {availableAdditionalDoneStates.map((doneState) => (
                                    <Select.Option key={doneState} value={doneState}>
                                        {doneState}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.Bitbucket: {
            const provider = integrationProvider as IntegrationProviderBitbucket;
            if (!provider) {
                return [];
            }

            function filter(inputValue, path) {
                return path.some((option) => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
            }

            return [
                {
                    label: `Workspace/Repository`,
                    initialValue:
                        integration.data?.selectedRepo?.id ?
                            [integration.data?.selectedRepo?.workspaceId, integration.data?.selectedRepo?.id]
                        :   null,
                    data: provider.data?.availableWorkspacesAndRepos ?? [],
                    name: 'workspaceIdAndRepoId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (workspacesAndRepos: BitbucketWorkspaceAndRepos[], extraProperties) => {
                        const cascaderOptions = workspacesAndRepos.map((workspaceAndRepos) => {
                            return {
                                value: workspaceAndRepos.workspaceId,
                                label: workspaceAndRepos.workspaceSlug,
                                disabled: !workspaceAndRepos.repos || workspaceAndRepos.repos?.length === 0,
                                children:
                                    workspaceAndRepos.repos?.map((repo) => {
                                        return {
                                            value: repo.id,
                                            label: repo.name,
                                            children: null,
                                        };
                                    }) ?? null,
                            };
                        });
                        return (
                            <Cascader
                                allowClear
                                options={cascaderOptions}
                                showSearch={{ filter }}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
            ];
        }
        case IntegrationType.BitbucketCI: {
            const provider = integrationProvider as IntegrationProviderBitbucket;
            if (!provider) {
                return [];
            }

            function filter(inputValue, path) {
                return path.some((option) => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
            }

            const fields = [
                {
                    label: `Workspace/Repository`,
                    initialValue:
                        integration.data?.selectedRepo?.id ?
                            [integration.data?.selectedRepo?.workspaceId, integration.data?.selectedRepo?.id]
                        :   null,
                    data: provider.data?.availableWorkspacesAndRepos ?? [],
                    name: 'workspaceIdAndRepoId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (workspacesAndRepos: BitbucketWorkspaceAndRepos[], extraProperties) => {
                        const cascaderOptions = workspacesAndRepos.map((workspaceAndRepos) => {
                            return {
                                value: workspaceAndRepos.workspaceId,
                                label: workspaceAndRepos.workspaceSlug,
                                disabled: !workspaceAndRepos.repos || workspaceAndRepos.repos?.length === 0,
                                children:
                                    workspaceAndRepos.repos?.map((repo) => {
                                        return {
                                            value: repo.id,
                                            label: repo.name,
                                            children: null,
                                        };
                                    }) ?? null,
                            };
                        });
                        return (
                            <Cascader
                                allowClear
                                options={cascaderOptions}
                                showSearch={{ filter }}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                {
                    label: isWorkflowCIStepEnabled ? `Pipeline` : `${rcBuildName} pipeline`,
                    initialValue: integration.data.workflowName,
                    data: [],
                    name: 'workflowId',
                    fieldType: FieldType.Input,
                    rules: [{ required: true }],
                },
                workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Staging),
            ];

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...[
                        {
                            label: 'Working branch pipeline',
                            initialValue: integration.data.devWorkflowId,
                            data: [],
                            name: 'devWorkflowId',
                            fieldType: FieldType.Input,
                            rules: [{ required: false }],
                            helpMessage: devWorkflowHelper,
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Working),
                        {
                            label: 'Production pipeline',
                            helpMessage: prodWorkflowHelperMessage,
                            initialValue: integration.data.prodWorkflowId,
                            data: [],
                            rules: [],
                            name: 'prodWorkflowId',
                            fieldType: FieldType.Input,
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Production),
                    ],
                );
            }

            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.CustomCI: {
            const customCIData = integration.data as CustomCIIntegrationData;
            const triggerHeaderKeys = customCIData?.triggerHeaderKeys ?? [];
            return [
                {
                    label: 'Build trigger URL (optional)',
                    helpMessage:
                        'If set, Runway will POST to this https URL to trigger a build. Leave empty to keep Custom CI upload-only.',
                    initialValue: customCIData?.triggerUrl ?? '',
                    name: 'triggerUrl',
                    fieldType: FieldType.Input,
                    placeholder: 'https://ci.example.com/trigger',
                    rules: [{ required: false }],
                },
                {
                    fieldType: FieldType.Custom,
                    name: 'triggerHeaders',
                    skipFormItemWrapper: true,
                    // Seed at the form level: the modal builds Form initialValues from each field's
                    // initialValue, and a Form-level value overrides a Form.List's own initialValue.
                    initialValue: initialTriggerHeaderRows(triggerHeaderKeys),
                    renderField: () => <CustomCITriggerHeadersEditor redactedHeaderKeys={triggerHeaderKeys} />,
                },
                {
                    label: 'Trigger request body (optional)',
                    helpMessage: (
                        <SupportedTokensComponent
                            availableTokens={BuildTriggerAvailableTokens}
                            prefixText="Body sent with the trigger request."
                        />
                    ),
                    initialValue: customCIData?.triggerBodyTemplate ?? '',
                    name: 'triggerBodyTemplate',
                    fieldType: FieldType.Custom,
                    rules: [{ required: false }],
                    renderField: () => (
                        <TextArea
                            rows={4}
                            placeholder={'{\n  "ref": "{releaseBranchName}",\n  "sha": "{commitShortHash}"\n}'}
                        />
                    ),
                },
                multiAPKField(integration.data),
            ];
        }
        case IntegrationType.GenericCI: {
            const provider = integrationProvider as IntegrationProviderGeneric;

            if (!provider) {
                return [];
            }

            const rcBuildName = platform === AppPlatform.ReactNativeOTA ? 'Staging' : 'Release Candidate';

            const fields: FormFieldData[] = [
                {
                    label: isWorkflowCIStepEnabled ? `Workflow` : `${rcBuildName} workflow`,
                    initialValue: integration.data?.workflowName,
                    data: [],
                    name: 'workflowId',
                    fieldType: FieldType.Input,
                    rules: [{ required: true }],
                },
            ];

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...[
                        {
                            label: `Development workflow`,
                            initialValue: integration.data?.devWorkflowId,
                            helpMessage: devWorkflowHelper,
                            data: [],
                            name: 'devWorkflowId',
                            fieldType: FieldType.Input,
                            rules: [{ required: false }],
                        },
                        {
                            label: 'Release workflow',
                            helpMessage: prodWorkflowHelperMessage,
                            initialValue: integration.data?.prodWorkflowId,
                            data: [],
                            rules: [],
                            name: 'prodWorkflowId',
                            fieldType: FieldType.Input,
                        },
                    ],
                );
            }

            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.GitLabVCS: {
            const provider = integrationProvider as IntegrationProviderGitLab;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: 'Project',
                    data: provider.data?.availableProjects,
                    initialValue: integration.data?.project?.projectId,
                    name: 'projectId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (projects: GitLabProject[]) => {
                        return (
                            <Select showSearch allowClear filterOption={filterOptionLabel}>
                                {projects.map((project) => (
                                    <Select.Option
                                        key={project.projectId}
                                        value={project.projectId}
                                        label={project.displayableName}
                                    >
                                        {project.displayableName}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.GitLabCI: {
            const provider = integrationProvider as IntegrationProviderGitLab;

            if (!provider) {
                return [];
            }

            const rcBuildName = platform === AppPlatform.ReactNativeOTA ? 'Staging' : 'Release Candidate';

            const fields: FormFieldData[] = [
                {
                    label: 'Project',
                    data: provider.data?.availableProjects,
                    initialValue: integration.data?.project?.projectId,
                    name: 'projectId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (projects: GitLabProject[]) => {
                        return (
                            <Select showSearch allowClear filterOption={filterOptionLabel}>
                                {projects.map((project) => (
                                    <Select.Option
                                        key={project.projectId}
                                        value={project.projectId}
                                        label={project.displayableName}
                                    >
                                        {project.displayableName}
                                    </Select.Option>
                                ))}
                                ReactNativeOTA
                            </Select>
                        );
                    },
                },
                {
                    label: isWorkflowCIStepEnabled ? `Deploy job` : `${rcBuildName} deploy job`,
                    initialValue: integration.data?.workflowName,
                    data: [],
                    name: 'workflowId',
                    fieldType: FieldType.Input,
                    rules: [{ required: true }],
                },
                workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Staging),
            ];

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...[
                        {
                            label: `Development deploy job`,
                            initialValue: integration.data?.devWorkflowId,
                            helpMessage: devWorkflowHelper,
                            data: [],
                            name: 'devWorkflowId',
                            fieldType: FieldType.Input,
                            rules: [{ required: false }],
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Working),
                        {
                            label: 'Release deploy job',
                            helpMessage: prodWorkflowHelperMessage,
                            initialValue: integration.data?.prodWorkflowId,
                            data: [],
                            rules: [],
                            name: 'prodWorkflowId',
                            fieldType: FieldType.Input,
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Production),
                    ],
                );
            }

            fields.push(
                ...strictWorkflowArgumentFilteringFields(
                    integration.data as BaseCIIntegrationData,
                    isWorkflowCIStepEnabled,
                ),
            );
            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.TestFlight: {
            const provider = integrationProvider as IntegrationProviderApple;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: 'Bundle ID',
                    initialValue: integration.data?.bundleId,
                    data: provider.data?.availableApps ?? [],
                    name: 'bundleId',
                    fieldType: FieldType.Custom,

                    renderField: (data: ASCAppInfo[]) => {
                        return (
                            <Select onChange={onChangeBundleId}>
                                {data.map((info) => {
                                    return (
                                        <Select.Option value={info.bundleId} key={info.bundleId}>
                                            {info.bundleId}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                },
            ];
        }
        case IntegrationType.Apple: {
            const provider = integrationProvider as IntegrationProviderApple;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: 'Bundle ID',
                    initialValue: integration.data?.bundleId,
                    data: provider.data?.availableApps ?? [],
                    name: 'bundleId',
                    fieldType: FieldType.Custom,

                    renderField: (data: ASCAppInfo[]) => {
                        return (
                            <Select>
                                {data.map((info) => {
                                    return (
                                        <Select.Option value={info.bundleId} key={info.bundleId}>
                                            {info.bundleId}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                },
            ];
        }
        case IntegrationType.AzureCI: {
            const provider = integrationProvider as IntegrationProviderAzure;

            if (!provider) {
                return [];
            }

            const fields: FormFieldData[] = [
                {
                    label:
                        isWorkflowCIStepEnabled ? `Azure project and pipeline` : (
                            `Azure project and ${rcBuildName} pipeline`
                        ),
                    initialValue:
                        integration.data?.workflowId ?
                            [integration.data?.selectedProject?.id, integration.data?.workflowId]
                        :   null,
                    data: provider.data?.availableProjectsAndResources ?? [],
                    name: 'projectIdAndWorkflowId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (projectsAndResources: AzureProjectAndResources[], extraProperties) => {
                        const cascaderOptions = projectsAndResources.map((projectAndResources) => {
                            return {
                                value: projectAndResources.project.id,
                                label: projectAndResources.project.name,
                                disabled: !projectAndResources.pipelines || projectAndResources.pipelines?.length === 0,
                                children:
                                    projectAndResources.pipelines?.map((pipeline) => {
                                        return {
                                            value: pipeline.id,
                                            label: pipeline.name,
                                            children: null,
                                        };
                                    }) ?? null,
                            };
                        });
                        return (
                            <Cascader
                                allowClear
                                options={cascaderOptions}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Staging),
            ];

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...[
                        {
                            label: 'Development pipeline',
                            initialValue:
                                integration.data?.devWorkflowId ?
                                    [integration.data?.selectedProject?.id, integration.data?.devWorkflowId]
                                :   null,
                            data: provider.data?.availableProjectsAndResources ?? [],
                            name: 'projectIdAndDevWorkflowId',
                            fieldType: FieldType.Custom,
                            helpMessage: devWorkflowHelper,
                            rules: [
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (value) {
                                            const project = value[0];
                                            const parentProject = getFieldValue('projectIdAndWorkflowId')?.[0];

                                            if (project && parentProject && project !== parentProject) {
                                                return Promise.reject(`Project must equal ${rcBuildName} project`);
                                            }
                                        }

                                        return Promise.resolve();
                                    },
                                }),
                            ],
                            extraFormItemProps: {
                                dependencies: ['projectIdAndWorkflowId'],
                            },

                            renderField: (projectsAndResources: AzureProjectAndResources[], extraProperties) => {
                                const cascaderOptions = projectsAndResources.map((projectAndResources) => {
                                    return {
                                        value: projectAndResources.project.id,
                                        label: projectAndResources.project.name,
                                        disabled:
                                            !projectAndResources.pipelines ||
                                            projectAndResources.pipelines?.length === 0,
                                        children:
                                            projectAndResources.pipelines?.map((pipeline) => {
                                                return {
                                                    value: pipeline.id,
                                                    label: pipeline.name,
                                                    children: null,
                                                };
                                            }) ?? null,
                                    };
                                });
                                return (
                                    <Cascader
                                        allowClear
                                        options={cascaderOptions}
                                        expandTrigger="hover"
                                        placeholder={'Select'}
                                        {...extraProperties}
                                    />
                                );
                            },
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Working),
                        {
                            label: 'Release pipeline',
                            helpMessage: prodWorkflowHelperMessage,
                            initialValue:
                                integration.data?.prodWorkflowId ?
                                    [integration.data?.selectedProject?.id, integration.data?.prodWorkflowId]
                                :   null,
                            data: provider.data?.availableProjectsAndResources ?? [],
                            name: 'projectIdAndProdWorkflowId',
                            fieldType: FieldType.Custom,
                            rules: [
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (value) {
                                            const project = value[0];
                                            const parentProject = getFieldValue('projectIdAndWorkflowId')?.[0];

                                            if (project && parentProject && project !== parentProject) {
                                                return Promise.reject(`Project must equal ${rcBuildName} project`);
                                            }
                                        }

                                        return Promise.resolve();
                                    },
                                }),
                            ],
                            extraFormItemProps: {
                                dependencies: ['projectIdAndWorkflowId'],
                            },

                            renderField: (projectsAndResources: AzureProjectAndResources[], extraProperties) => {
                                const cascaderOptions = projectsAndResources.map((projectAndResources) => {
                                    return {
                                        value: projectAndResources.project.id,
                                        label: projectAndResources.project.name,
                                        disabled:
                                            !projectAndResources.pipelines ||
                                            projectAndResources.pipelines?.length === 0,
                                        children:
                                            projectAndResources.pipelines?.map((pipeline) => {
                                                return {
                                                    value: pipeline.id,
                                                    label: pipeline.name,
                                                    children: null,
                                                };
                                            }) ?? null,
                                    };
                                });
                                return (
                                    <Cascader
                                        allowClear
                                        options={cascaderOptions}
                                        expandTrigger="hover"
                                        placeholder={'Select'}
                                        {...extraProperties}
                                    />
                                );
                            },
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Production),
                    ],
                );
            }

            fields.push(
                ...strictWorkflowArgumentFilteringFields(
                    integration.data as BaseCIIntegrationData,
                    isWorkflowCIStepEnabled,
                ),
            );
            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.AzureIT: {
            const provider = integrationProvider as IntegrationProviderAzure;

            if (!provider) {
                return [];
            }

            const workAreas =
                azureProjectId ?
                    provider.data.availableProjectsAndResources.find((info) => info.project.id === azureProjectId)
                        ?.workAreas
                :   [];

            const boardColumns =
                azureProjectId ?
                    provider.data.availableProjectsAndResources.find((info) => info.project.id === azureProjectId)
                        ?.boardColumns
                :   [];

            return [
                {
                    label: 'Project',
                    initialValue: integration.data?.selectedProject?.id,
                    data: provider.data?.availableProjectsAndResources ?? [],
                    name: 'projectId',
                    fieldType: FieldType.Custom,

                    renderField: (data: AzureProjectAndResources[]) => {
                        return (
                            <Select onChange={onChangeAzureProjectId}>
                                {data.map((info) => {
                                    return (
                                        <Select.Option value={info.project.id} key={info.project.id}>
                                            {info.project.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                },
                {
                    label: 'Work areas',
                    initialValue:
                        integration.data?.selectedWorkAreas?.map((wa) => {
                            return wa.id;
                        }) ?? [],
                    name: 'projects',
                    fieldType: FieldType.Custom,
                    data: workAreas,

                    renderField: (data: AzureWorkArea[]) => {
                        return (
                            <Select mode="multiple" placeholder="Select" filterOption={filterOptionLabel}>
                                {data.map((workArea) => {
                                    return (
                                        <Select.Option value={workArea.id} key={workArea.id} label={workArea.path}>
                                            {workArea.path}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: false }],
                    helpMessage: 'If needed, limit to specific Azure work areas in scope for this app',
                },
                {
                    label: 'Additional done columns',
                    initialValue:
                        integration.data?.additionalDoneColumns?.map((dc) => {
                            return dc.name;
                        }) ?? [],
                    name: 'additionalDoneStatuses',
                    fieldType: FieldType.Custom,
                    data: boardColumns,

                    renderField: (data: AzureBoardColumn[]) => {
                        return (
                            <Select mode="multiple" placeholder="Select" filterOption={filterOptionLabel}>
                                {data.map((column) => {
                                    return (
                                        <Select.Option value={column.name} key={column.name} label={column.name}>
                                            {column.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: false }],
                    helpMessage: 'Specify additional columns that should be treated as "done"',
                },
            ];
        }
        case IntegrationType.Amazon: {
            return [
                {
                    label: 'App ID',
                    name: 'appId',
                    initialValue: integration.data?.selectedAppId,
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
            ];
        }
        case IntegrationType.Google: {
            return [
                {
                    label: 'Package name',
                    name: 'packageName',
                    initialValue: integration.data?.packageName,
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
                {
                    label: 'Developer account ID',
                    name: 'developerId',
                    initialValue: integration.data?.developerId,
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '12345',
                    helpMessage:
                        'You can find your developer account ID under Account Details in the Google Play Console.',
                },
                {
                    label: (
                        <>
                            Play Console app ID
                            <Tooltip
                                title={
                                    <>
                                        Including your app ID allows Runway to include direct links to app reviews in
                                        various features (for example, review notifications).
                                        <br />
                                        <br />
                                        You can find this ID in the URL of any Play Console page for this app, right
                                        after /app/.
                                    </>
                                }
                            >
                                <InfoCircleOutlined style={{ marginLeft: 6, color: antGrey.grey6 }} />
                            </Tooltip>
                        </>
                    ),
                    name: 'consoleAppId',
                    initialValue: integration.data?.consoleAppId,
                    rules: [{ required: false }],
                    fieldType: FieldType.Input,
                    placeholder: '12345',
                },
            ];
        }
        case IntegrationType.HuaweiAppGallery: {
            return [
                {
                    label: 'App ID',
                    name: 'appId',
                    initialValue: integration.data?.appId,
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
            ];
        }
        case IntegrationType.Samsung: {
            const provider = integrationProvider as IntegrationProviderSamsung;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: 'Content ID',
                    name: 'contentId',
                    initialValue: integration.data?.appId,
                    rules: [{ required: true }],
                    data: provider.data?.availableApps ?? [],
                    fieldType: FieldType.Custom,
                    renderField: (data: SamsungApp[]) => {
                        return (
                            <Select>
                                {data.map((app) => {
                                    return (
                                        <Select.Option value={app.contentId} key={app.contentId}>
                                            {app.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.GoogleBeta: {
            const fields: FormFieldData[] = [
                {
                    label: 'Package name',
                    name: 'packageName',
                    initialValue: integration.data?.packageName,
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
                {
                    label: 'Developer account ID',
                    name: 'developerId',
                    initialValue: integration.data?.developerId,
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '12345',
                    helpMessage:
                        'You can find your developer account ID under Account Details in the Google Play Console.',
                },
            ];

            // If beta testing is WF-enabled, track name lives in the step config instead of integration settings
            if (!workflowEnabledSteps[ReleaseStepType.BetaTesting]) {
                fields.push({
                    label: 'Beta testing track',
                    name: 'testingTrack',
                    initialValue: integration.data?.testingTrack,
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    helpMessage: "Choose from 'alpha', 'beta', 'internal', or another custom testing track",
                });
            }

            return fields;
        }
        case IntegrationType.FirebaseBeta: {
            const provider = integrationProvider as IntegrationProviderGoogle;

            if (!provider) {
                return [];
            }

            const projectId = firebaseProjectId ?? integration.data?.projectId;

            const resourcesForProject =
                projectId ?
                    provider.data?.projectsResources?.find((info) => info.project.projectId === projectId)
                :   null;

            // Not filtered by app platform: a destination can target a platform other than the app's,
            // and the option label already spells out each app's platform via its Firebase app id.
            const apps: FirebaseApp[] = [
                ...(resourcesForProject?.availableIosApps?.map(firebaseAppFromIosApp) ?? []),
                ...(resourcesForProject?.availableAndroidApps?.map(firebaseAppFromAndroidApp) ?? []),
            ];

            return [
                {
                    label: 'Selected Firebase Project',
                    name: 'projectId',
                    initialValue: integration.data?.projectId,
                    rules: [{ required: true }],
                    fieldType: FieldType.Custom,
                    helpMessage: 'The Project ID can be found under Project Settings > General in the Firebase Console',
                    data: provider.data?.projectsResources ?? [],
                    renderField: (data: FirebaseProjectResources[]) => {
                        return (
                            <Select
                                allowClear
                                className="select-reserve-clear-space"
                                placeholder="Select Firebase project"
                                filterOption={filterOptionLabel}
                                onChange={onChangeFirebaseProjectId}
                                popupMatchSelectWidth={false}
                            >
                                {data?.map((info) => (
                                    <Select.Option
                                        key={info.project.projectId}
                                        value={info.project.projectId}
                                        label={info.project.displayName || info.project.projectId}
                                    >
                                        {`${info.project.displayName} - ${info.project.projectId}`}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Selected Firebase App',
                    name: 'firebaseAppId',
                    initialValue: integration.data?.firebaseAppId,
                    rules: [{ required: true }],
                    fieldType: FieldType.Custom,
                    helpMessage:
                        'The Firebase App ID can be found under Project Settings > General > My Apps in the Firebase Console',
                    data: apps,
                    renderField: (apps: FirebaseApp[]) => {
                        return (
                            <Select
                                allowClear
                                className="select-reserve-clear-space"
                                placeholder="Select Firebase app"
                                filterOption={filterOptionLabel}
                                popupMatchSelectWidth={false}
                            >
                                {apps?.map((app) => (
                                    <Select.Option
                                        key={app.appId}
                                        value={app.appId}
                                        label={app.displayName || app.appId}
                                    >
                                        {`${app.displayName} - ${app.appId} (${app.bundleOrPackageName})`}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.Codemagic: {
            const provider = integrationProvider as IntegrationProviderCodemagic;

            if (!provider) {
                return [];
            }

            const fields: FormFieldData[] = [
                {
                    label: isWorkflowCIStepEnabled ? `Workflow` : `${rcBuildName} workflow`,
                    initialValue:
                        integration.data?.workflowId ? [integration.data?.appId, integration.data?.workflowId] : null,
                    data: provider.data?.availableApps ?? [],
                    name: 'slugAndWorkflowId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (apps: CodemagicAppData[], extraProperties) => {
                        return (
                            <Cascader
                                allowClear
                                options={codemagicCascaderOptions(apps)}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Staging),
            ];

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...[
                        {
                            label: `Working branch workflow`,
                            initialValue:
                                integration.data?.devWorkflowId ?
                                    [integration.data?.devAppId, integration.data?.devWorkflowId]
                                :   null,
                            data: provider.data?.availableApps ?? [],
                            name: 'slugAndDevWorkflowId',
                            fieldType: FieldType.Custom,
                            helpMessage: devWorkflowHelper,
                            rules: [{ required: false }],

                            renderField: (apps: CodemagicAppData[], extraProperties) => {
                                return (
                                    <Cascader
                                        allowClear
                                        options={codemagicCascaderOptions(apps)}
                                        expandTrigger="hover"
                                        placeholder={'Select'}
                                        {...extraProperties}
                                    />
                                );
                            },
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Working),
                        {
                            label: prodWorkflowLabel,
                            helpMessage: prodWorkflowHelperMessage,
                            initialValue:
                                integration.data?.prodWorkflowId ?
                                    [integration.data?.prodAppId, integration.data?.prodWorkflowId]
                                :   null,
                            data: provider.data?.availableApps ?? [],
                            name: 'slugAndProdWorkflowId',
                            fieldType: FieldType.Custom,
                            rules: [{ required: false }],

                            renderField: (apps: CodemagicAppData[], extraProperties) => {
                                return (
                                    <Cascader
                                        allowClear
                                        options={codemagicCascaderOptions(apps)}
                                        expandTrigger="hover"
                                        placeholder={'Select'}
                                        {...extraProperties}
                                    />
                                );
                            },
                            placeholder: 'Select',
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Production),
                    ],
                );
            }

            fields.push(
                ...strictWorkflowArgumentFilteringFields(
                    integration.data as BaseCIIntegrationData,
                    isWorkflowCIStepEnabled,
                ),
            );
            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.Bitrise: {
            const provider = integrationProvider as IntegrationProviderBitrise;

            if (!provider) {
                return [];
            }

            const fieldsForWorkflowType = (workflowType: WorkflowType) => {
                let isPipelineInitial = false;
                if (integration.data?.isPipeline && integration.data?.isPipeline[workflowType] == true) {
                    isPipelineInitial = true;
                }

                let isPipeline: boolean;
                let workflowTypeString: string;
                let rules: Rule[];
                switch (workflowType) {
                    case WorkflowType.Staging:
                        isPipeline = bitriseIsPipeline ?? false;
                        workflowTypeString = '';
                        rules = [{ required: true }];
                        break;
                    case WorkflowType.Working:
                        isPipeline = bitriseIsPipelineDev ?? false;
                        workflowTypeString = 'Dev';
                        rules = [{ required: false }];
                        break;
                    case WorkflowType.Production:
                        isPipeline = bitriseIsPipelineProd ?? false;
                        workflowTypeString = 'Prod';
                        rules = [{ required: false }];
                        break;
                }

                const fields: FormFieldData[] = [
                    {
                        label: 'Type',
                        name: 'bitriseIsPipeline' + workflowTypeString,
                        initialValue: isPipelineInitial,
                        fieldType: FieldType.Select,
                        data: [
                            { value: false, displayValue: 'Workflow' },
                            { value: true, displayValue: 'Pipeline' },
                        ],
                    },
                ];

                if (isPipeline == true) {
                    let appLabel: string;
                    let pipelineLabel: string;
                    let appInitialValue: any | null;
                    let pipelineInitialValue: any | null;
                    let helpMessage: string | null = null;
                    switch (workflowType) {
                        case WorkflowType.Staging:
                            appLabel = isWorkflowCIStepEnabled ? `App` : `${rcBuildName} app`;
                            pipelineLabel = isWorkflowCIStepEnabled ? `Pipeline` : `${rcBuildName} pipeline`;
                            appInitialValue = integration.data?.appSlug;
                            pipelineInitialValue = isPipelineInitial ? integration.data?.workflowId : null;
                            break;
                        case WorkflowType.Working:
                            appLabel = `Working branch app`;
                            pipelineLabel = `Working branch pipeline`;
                            appInitialValue = integration.data?.devAppSlug;
                            pipelineInitialValue = isPipelineInitial ? integration.data?.devWorkflowId : null;
                            helpMessage = devWorkflowHelperMessage('pipeline');
                            break;
                        case WorkflowType.Production:
                            appLabel = `Release app`;
                            pipelineLabel = `Release pipeline`;
                            appInitialValue = integration.data?.prodAppSlug;
                            pipelineInitialValue = isPipelineInitial ? integration.data?.prodWorkflowId : null;
                            helpMessage = prodWorkflowHelperMessageForPlatform(platform, 'pipeline');
                            break;
                    }

                    fields.push({
                        label: appLabel,
                        initialValue: appInitialValue,
                        data: provider.data?.availableApps ?? [],
                        name: 'appSlug' + workflowTypeString,
                        fieldType: FieldType.Custom,
                        rules: rules,

                        renderField: (apps: BitriseAppData[], _) => {
                            return (
                                <Select showSearch allowClear filterOption={filterOptionLabel}>
                                    {apps.map((app) => (
                                        <Select.Option key={app.slug} value={app.slug} label={app.title}>
                                            {app.title}
                                        </Select.Option>
                                    ))}
                                </Select>
                            );
                        },
                    });

                    fields.push({
                        label: pipelineLabel,
                        initialValue: pipelineInitialValue,
                        name: 'workflowId' + workflowTypeString,
                        fieldType: FieldType.Input,
                        helpMessage: helpMessage,
                        rules: rules,
                    });
                } else {
                    let label: string;
                    let initialValue: any | null;
                    let helpMessage: string | null = null;
                    switch (workflowType) {
                        case WorkflowType.Staging:
                            label = isWorkflowCIStepEnabled ? `Workflow` : `${rcBuildName} workflow`;
                            initialValue =
                                !isPipelineInitial && integration.data?.workflowId ?
                                    [integration.data?.appSlug, integration.data?.workflowId]
                                :   null;
                            break;
                        case WorkflowType.Working:
                            label = `Working branch workflow`;
                            initialValue =
                                !isPipelineInitial && integration.data?.devWorkflowId ?
                                    [integration.data?.devAppSlug, integration.data?.devWorkflowId]
                                :   null;
                            helpMessage = devWorkflowHelper;
                            break;
                        case WorkflowType.Production:
                            label = prodWorkflowLabel;
                            initialValue =
                                !isPipelineInitial && integration.data?.prodWorkflowId ?
                                    [integration.data?.prodAppSlug, integration.data?.prodWorkflowId]
                                :   null;
                            helpMessage = prodWorkflowHelperMessage;
                            break;
                    }

                    fields.push({
                        label: label,
                        initialValue: initialValue,
                        data: provider.data?.availableApps ?? [],
                        name: 'slugAnd' + workflowTypeString + 'WorkflowId',
                        fieldType: FieldType.Custom,
                        helpMessage: helpMessage,
                        rules: rules,

                        renderField: (apps: BitriseAppData[], extraProperties) => {
                            return (
                                <Cascader
                                    allowClear
                                    options={bitriseCascaderOptions(apps)}
                                    expandTrigger="hover"
                                    placeholder={'Select'}
                                    {...extraProperties}
                                />
                            );
                        },
                    });
                }

                fields.push(workflowArgumentFieldForWorkflowType(workflowArguments, workflowType));

                return fields;
            };

            const fields = fieldsForWorkflowType(WorkflowType.Staging);

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...fieldsForWorkflowType(WorkflowType.Working),
                    ...fieldsForWorkflowType(WorkflowType.Production),
                );
            }

            fields.push(
                ...strictWorkflowArgumentFilteringFields(
                    integration.data as BaseCIIntegrationData,
                    isWorkflowCIStepEnabled,
                ),
            );
            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.Buildkite: {
            const provider = integrationProvider as IntegrationProviderBuildkite;

            if (!provider) {
                return [];
            }

            const fields: FormFieldData[] = [
                {
                    label:
                        isWorkflowCIStepEnabled ? `Buildkite org and pipeline` : (
                            `Buildkite org and ${rcBuildName} pipeline`
                        ),
                    initialValue:
                        integration.data?.workflowId ? [integration.data?.org?.id, integration.data?.workflowId] : null,
                    data: provider.data?.availableOrgsAndPipelines ?? [],
                    name: 'orgIdAndWorkflowId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (orgsAndPipelines: BuildkiteOrgAndPipelines[], extraProperties) => {
                        const cascaderOptions = orgsAndPipelines.map((orgAndPipelines) => {
                            return {
                                value: orgAndPipelines.org.id,
                                label: `${orgAndPipelines.org.name} (${orgAndPipelines.org.slug})`,
                                disabled: !orgAndPipelines.pipelines || orgAndPipelines.pipelines?.length === 0,
                                children:
                                    orgAndPipelines.pipelines?.map((pipeline) => {
                                        return {
                                            value: pipeline.slug,
                                            label: pipeline.name,
                                            children: null,
                                        };
                                    }) ?? null,
                            };
                        });
                        return (
                            <Cascader
                                allowClear
                                options={cascaderOptions}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Staging),
            ];

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...[
                        {
                            label: 'Development pipeline',
                            initialValue:
                                integration.data?.devWorkflowId ?
                                    [integration.data?.org?.id, integration.data?.devWorkflowId]
                                :   null,
                            data: provider.data?.availableOrgsAndPipelines ?? [],
                            name: 'orgIdAndDevWorkflowId',
                            fieldType: FieldType.Custom,
                            helpMessage: devWorkflowHelper,
                            rules: [
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (value) {
                                            const org = value[0];
                                            const parentOrg = getFieldValue('orgIdAndWorkflowId')?.[0];

                                            if (org && parentOrg && org !== parentOrg) {
                                                return Promise.reject(`Org must equal ${rcBuildName} org`);
                                            }
                                        }

                                        return Promise.resolve();
                                    },
                                }),
                            ],
                            extraFormItemProps: {
                                dependencies: ['orgIdAndWorkflowId'],
                            },

                            renderField: (orgsAndPipelines: BuildkiteOrgAndPipelines[], extraProperties) => {
                                const cascaderOptions = orgsAndPipelines.map((orgAndPipelines) => {
                                    return {
                                        value: orgAndPipelines.org.id,
                                        label: `${orgAndPipelines.org.name} (${orgAndPipelines.org.slug})`,
                                        disabled: !orgAndPipelines.pipelines || orgAndPipelines.pipelines?.length === 0,
                                        children:
                                            orgAndPipelines.pipelines?.map((pipeline) => {
                                                return {
                                                    value: pipeline.slug,
                                                    label: pipeline.name,
                                                    children: null,
                                                };
                                            }) ?? null,
                                    };
                                });
                                return (
                                    <Cascader
                                        allowClear
                                        options={cascaderOptions}
                                        expandTrigger="hover"
                                        placeholder={'Select'}
                                        {...extraProperties}
                                    />
                                );
                            },
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Working),
                        {
                            label: 'Release pipeline',
                            helpMessage: prodWorkflowHelperMessage,
                            initialValue:
                                integration.data?.prodWorkflowId ?
                                    [integration.data?.org?.id, integration.data?.prodWorkflowId]
                                :   null,
                            data: provider.data?.availableOrgsAndPipelines ?? [],
                            name: 'orgIdAndProdWorkflowId',
                            fieldType: FieldType.Custom,
                            rules: [
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (value) {
                                            const org = value[0];
                                            const parentOrg = getFieldValue('orgIdAndWorkflowId')?.[0];

                                            if (org && parentOrg && org !== parentOrg) {
                                                return Promise.reject(`Org must equal ${rcBuildName} org`);
                                            }
                                        }

                                        return Promise.resolve();
                                    },
                                }),
                            ],
                            extraFormItemProps: {
                                dependencies: ['orgIdAndWorkflowId'],
                            },

                            renderField: (orgsAndPipelines: BuildkiteOrgAndPipelines[], extraProperties) => {
                                const cascaderOptions = orgsAndPipelines.map((orgAndPipelines) => {
                                    return {
                                        value: orgAndPipelines.org.id,
                                        label: `${orgAndPipelines.org.name} (${orgAndPipelines.org.slug})`,
                                        disabled: !orgAndPipelines.pipelines || orgAndPipelines.pipelines?.length === 0,
                                        children:
                                            orgAndPipelines.pipelines?.map((pipeline) => {
                                                return {
                                                    value: pipeline.slug,
                                                    label: pipeline.name,
                                                    children: null,
                                                };
                                            }) ?? null,
                                    };
                                });
                                return (
                                    <Cascader
                                        allowClear
                                        options={cascaderOptions}
                                        expandTrigger="hover"
                                        placeholder={'Select'}
                                        {...extraProperties}
                                    />
                                );
                            },
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Production),
                    ],
                );
            }

            fields.push(
                ...strictWorkflowArgumentFilteringFields(
                    integration.data as BaseCIIntegrationData,
                    isWorkflowCIStepEnabled,
                ),
            );
            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.Bugsnag: {
            const provider = integrationProvider as IntegrationProviderBugsnag;

            if (!provider) {
                return [];
            }

            function filter(inputValue, path) {
                return path.some((option) => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
            }

            return [
                {
                    label: `Organization and project`,
                    initialValue:
                        integration.data?.selectedProject?.id ?
                            [integration.data?.selectedProject?.orgId, integration.data?.selectedProject?.id]
                        :   null,
                    data: provider.data?.availableOrgsAndProjects ?? [],
                    name: 'orgIdAndProjectId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (orgsAndProjects: BugsnagOrgAndProjects[], extraProperties) => {
                        const cascaderOptions = orgsAndProjects.map((orgAndProjects) => {
                            return {
                                value: orgAndProjects.orgId,
                                label: orgAndProjects.orgName,
                                disabled: !orgAndProjects.projects || orgAndProjects.projects?.length === 0,
                                children:
                                    orgAndProjects.projects?.map((project) => {
                                        return {
                                            value: project.id,
                                            label: project.name,
                                            children: null,
                                        };
                                    }) ?? null,
                            };
                        });
                        return (
                            <Cascader
                                allowClear
                                options={cascaderOptions}
                                showSearch={{ filter }}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                {
                    label: `Prod release stage`,
                    data: provider.data?.availableOrgsAndProjects?.map((orgAndProjects) => {
                        const projects = orgAndProjects.projects ?? [];

                        return {
                            value: orgAndProjects.orgId,
                            label: orgAndProjects.orgName,
                            disabled: projects.length === 0,
                            children:
                                projects.map((project) => {
                                    return {
                                        value: project.id,
                                        label: project.name,
                                        children:
                                            project.releaseStages?.map((stage) => {
                                                return {
                                                    value: stage,
                                                    label: stage,
                                                };
                                            }) ?? null,
                                    };
                                }) ?? null,
                        };
                    }),
                    initialValue:
                        integration.data?.selectedProject && integration.data?.prodReleaseStage ?
                            [
                                integration.data?.selectedProject.orgId,
                                integration.data?.selectedProject.id,
                                integration.data?.prodReleaseStage,
                            ]
                        :   null,
                    name: 'orgIdAndProjectIdAndProdStage',
                    fieldType: FieldType.Custom,
                    rules: [
                        { required: true },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (value) {
                                    const orgId = value[0];
                                    const projectId = value[1];
                                    const parentOrgId = getFieldValue('orgIdAndProjectId')[0];
                                    const parentProjectId = getFieldValue('orgIdAndProjectId')[1];

                                    if (
                                        orgId &&
                                        projectId &&
                                        parentOrgId &&
                                        parentProjectId &&
                                        (orgId !== parentOrgId || projectId !== parentProjectId)
                                    ) {
                                        return Promise.reject(`Project selection must match`);
                                    }
                                }

                                return Promise.resolve();
                            },
                        }),
                    ],
                    extraFormItemProps: {
                        dependencies: ['orgIdAndProjectId'],
                    },

                    renderField: (data, extraProperties) => {
                        return (
                            <Cascader
                                options={data}
                                allowClear
                                showSearch={{ filter }}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                {
                    label: `Beta release stage`,
                    data: provider.data?.availableOrgsAndProjects?.map((orgAndProjects) => {
                        const projects = orgAndProjects.projects ?? [];

                        return {
                            value: orgAndProjects.orgId,
                            label: orgAndProjects.orgName,
                            disabled: projects.length === 0,
                            children:
                                projects.map((project) => {
                                    return {
                                        value: project.id,
                                        label: project.name,
                                        children:
                                            project.releaseStages?.map((stage) => {
                                                return {
                                                    value: stage,
                                                    label: stage,
                                                };
                                            }) ?? null,
                                    };
                                }) ?? null,
                        };
                    }),
                    initialValue:
                        integration.data?.selectedProject && integration.data?.betaReleaseStage ?
                            [
                                integration.data?.selectedProject.orgId,
                                integration.data?.selectedProject.id,
                                integration.data?.betaReleaseStage,
                            ]
                        :   null,
                    name: 'orgIdAndProjectIdAndBetaStage',
                    fieldType: FieldType.Custom,
                    helpMessage: 'If you have a Beta Testing integration connected, specify your beta release stage',
                    rules: [
                        { required: false },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (value) {
                                    const orgId = value[0];
                                    const projectId = value[1];
                                    const parentOrgId = getFieldValue('orgIdAndProjectId')[0];
                                    const parentProjectId = getFieldValue('orgIdAndProjectId')[1];

                                    if (
                                        orgId &&
                                        projectId &&
                                        parentOrgId &&
                                        parentProjectId &&
                                        (orgId !== parentOrgId || projectId !== parentProjectId)
                                    ) {
                                        return Promise.reject(`Project selection must match`);
                                    }
                                }

                                return Promise.resolve();
                            },
                        }),
                    ],
                    extraFormItemProps: {
                        dependencies: ['orgIdAndProjectId'],
                    },

                    renderField: (data, extraProperties) => {
                        return (
                            <Cascader
                                options={data}
                                allowClear
                                showSearch={{ filter }}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
            ];
        }
        case IntegrationType.Sentry: {
            const provider = integrationProvider as IntegrationProviderSentry;

            if (!provider) {
                return [];
            }

            function filter(inputValue, path) {
                return path.some((option) => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
            }

            return [
                {
                    label: `Organization and project`,
                    initialValue:
                        integration.data?.selectedProject?.id ?
                            [integration.data?.selectedProject?.orgId, integration.data?.selectedProject?.id]
                        :   null,
                    data: provider.data?.availableOrgsAndProjects ?? [],
                    name: 'orgIdAndProjectId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (orgsAndProjects: SentryOrgAndProjects[], extraProperties) => {
                        const cascaderOptions = orgsAndProjects.map((orgAndProjects) => {
                            return {
                                value: orgAndProjects.orgId,
                                label: orgAndProjects.orgName,
                                disabled: !orgAndProjects.projects || orgAndProjects.projects?.length === 0,
                                children:
                                    orgAndProjects.projects?.map((project) => {
                                        return {
                                            value: project.id,
                                            label: project.displayableName,
                                            children: null,
                                        };
                                    }) ?? null,
                            };
                        });
                        return (
                            <Cascader
                                allowClear
                                options={cascaderOptions}
                                showSearch={{ filter }}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                {
                    label: `Production environment`,
                    data: provider.data?.availableOrgsAndProjects?.map((orgAndProjects) => {
                        const projects = orgAndProjects.projects ?? [];

                        return {
                            value: orgAndProjects.orgId,
                            label: orgAndProjects.orgName,
                            disabled: projects.length === 0,
                            children:
                                projects.map((project) => {
                                    return {
                                        value: project.id,
                                        label: project.displayableName,
                                        children:
                                            project.environments?.map((environment) => {
                                                return {
                                                    value: environment,
                                                    label: environment,
                                                };
                                            }) ?? null,
                                    };
                                }) ?? null,
                        };
                    }),
                    initialValue:
                        integration.data?.selectedProject && integration.data?.prodEnvironment ?
                            [
                                integration.data?.selectedProject.orgId,
                                integration.data?.selectedProject.id,
                                integration.data?.prodEnvironment,
                            ]
                        :   null,
                    name: 'orgIdAndProjectIdAndProdEnvironment',
                    fieldType: FieldType.Custom,
                    rules: [
                        { required: true },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (value) {
                                    const orgId = value[0];
                                    const projectId = value[1];
                                    const parentOrgId = getFieldValue('orgIdAndProjectId')[0];
                                    const parentProjectId = getFieldValue('orgIdAndProjectId')[1];

                                    if (
                                        orgId &&
                                        projectId &&
                                        parentOrgId &&
                                        parentProjectId &&
                                        (orgId !== parentOrgId || projectId !== parentProjectId)
                                    ) {
                                        return Promise.reject(`Project selection must match`);
                                    }
                                }

                                return Promise.resolve();
                            },
                        }),
                    ],
                    extraFormItemProps: {
                        dependencies: ['orgIdAndProjectId'],
                    },

                    renderField: (data, extraProperties) => {
                        return (
                            <Cascader
                                options={data}
                                allowClear
                                showSearch={{ filter }}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                {
                    label: `Beta environment`,
                    data: provider.data?.availableOrgsAndProjects?.map((orgAndProjects) => {
                        const projects = orgAndProjects.projects ?? [];

                        return {
                            value: orgAndProjects.orgId,
                            label: orgAndProjects.orgName,
                            disabled: projects.length === 0,
                            children:
                                projects.map((project) => {
                                    return {
                                        value: project.id,
                                        label: project.displayableName,
                                        children:
                                            project.environments?.map((environment) => {
                                                return {
                                                    value: environment,
                                                    label: environment,
                                                };
                                            }) ?? null,
                                    };
                                }) ?? null,
                        };
                    }),
                    initialValue:
                        integration.data?.selectedProject && integration.data?.betaEnvironment ?
                            [
                                integration.data?.selectedProject.orgId,
                                integration.data?.selectedProject.id,
                                integration.data?.betaEnvironment,
                            ]
                        :   null,
                    name: 'orgIdAndProjectIdAndBetaEnvironment',
                    fieldType: FieldType.Custom,
                    helpMessage: 'If you have a Beta Testing integration connected, specify your beta environment',
                    rules: [
                        { required: false },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (value) {
                                    const orgId = value[0];
                                    const projectId = value[1];
                                    const parentOrgId = getFieldValue('orgIdAndProjectId')[0];
                                    const parentProjectId = getFieldValue('orgIdAndProjectId')[1];

                                    if (
                                        orgId &&
                                        projectId &&
                                        parentOrgId &&
                                        parentProjectId &&
                                        (orgId !== parentOrgId || projectId !== parentProjectId)
                                    ) {
                                        return Promise.reject(`Project selection must match`);
                                    }
                                }

                                return Promise.resolve();
                            },
                        }),
                    ],
                    extraFormItemProps: {
                        dependencies: ['orgIdAndProjectId'],
                    },

                    renderField: (data, extraProperties) => {
                        return (
                            <Cascader
                                options={data}
                                allowClear
                                showSearch={{ filter }}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
            ];
        }
        case IntegrationType.Jenkins: {
            const provider = integrationProvider as IntegrationProviderJenkins;
            const integrationData = integration.data as JenkinsIntegrationData;
            if (!provider || !integrationData) {
                return [];
            }

            const subJobHelperMessage =
                'Leave blank unless your ' +
                rcBuildName.toLowerCase() +
                ' builds are generated at subjob level, i.e. <Jenkins URL>/job/<workflow>/job/<subjob>/job/<branch>';
            const prodSubJobHelperMessage =
                'Leave blank unless your final release builds are generated at subjob level (see above)';

            const fields: FormFieldData[] = [
                {
                    label: isWorkflowCIStepEnabled ? `Job/workflow` : `${rcBuildName} job/workflow`,
                    initialValue: integrationData.workflowName != '' ? integrationData.workflowName : null,
                    data:
                        provider.data?.availableJobsOrWorkflows?.map((workflow) => {
                            return {
                                value: workflow,
                                displayValue: workflow,
                            };
                        }) ?? [],
                    name: 'workflowId',
                    fieldType: FieldType.Select,
                    rules: [{ required: true }],
                    placeholder: 'Select',
                },
                {
                    label: `${rcBuildName} subjob`,
                    helpMessage: subJobHelperMessage,
                    initialValue: integrationData.subJobName,
                    name: 'subWorkflowId',
                    fieldType: FieldType.Input,
                    rules: [{ required: false }],
                },
                workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Staging),
            ];

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...[
                        {
                            label: 'Development job/workflow',
                            initialValue: integrationData.devWorkflowId,
                            data:
                                provider.data?.availableJobsOrWorkflows?.map((workflow) => {
                                    return {
                                        value: workflow,
                                        displayValue: workflow,
                                    };
                                }) ?? [],
                            name: 'devWorkflowId',
                            fieldType: FieldType.Select,
                            rules: [{ required: false }],
                            placeholder: 'Select',
                            helpMessage: devWorkflowHelper,
                            inputProps: {
                                allowClear: true,
                            },
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Working),
                        {
                            label: 'Development subjob',
                            helpMessage: prodSubJobHelperMessage,
                            initialValue: integrationData.devSubJobName,
                            name: 'devSubWorkflowId',
                            rules: [{ required: false }],
                            fieldType: FieldType.Input,
                        },
                        {
                            label: 'Release job/workflow',
                            helpMessage: prodWorkflowHelperMessage,
                            initialValue: integrationData.prodWorkflowId,
                            data:
                                provider.data?.availableJobsOrWorkflows?.map((workflow) => {
                                    return {
                                        value: workflow,
                                        displayValue: workflow,
                                    };
                                }) ?? [],
                            name: 'prodWorkflowId',
                            fieldType: FieldType.Select,
                            rules: [{ required: false }],
                            placeholder: 'Select',
                            inputProps: {
                                allowClear: true,
                            },
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Production),
                        {
                            label: 'Release subjob',
                            helpMessage: prodSubJobHelperMessage,
                            initialValue: integrationData.prodSubJobName,
                            name: 'prodSubWorkflowId',
                            rules: [{ required: false }],
                            fieldType: FieldType.Input,
                        },
                    ],
                );
            }

            fields.push(
                ...strictWorkflowArgumentFilteringFields(
                    integration.data as BaseCIIntegrationData,
                    isWorkflowCIStepEnabled,
                ),
            );
            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.Jira: {
            const provider = integrationProvider as IntegrationProviderJira;

            if (!provider) {
                return [];
            }

            const hasExistingDoneStatuses = provider.data.doneStatuses.length > 0;

            return [
                {
                    label: 'Projects',
                    initialValue: integration.data.selectedProjects.map((project) => project.key),
                    data: provider.data.availableProjects,
                    name: 'projects',
                    fieldType: FieldType.Custom,
                    rules: [
                        { required: false },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const autoSelectAllProjects = getFieldValue('autoSelectAllProjects');
                                if ((!value || value.length == 0) && autoSelectAllProjects != true) {
                                    return Promise.reject(`You must select at least one project`);
                                }

                                return Promise.resolve();
                            },
                        }),
                    ],

                    renderField: (availableProjects: JiraProject[]) => {
                        return (
                            <Select
                                allowClear
                                mode="multiple"
                                placeholder="Select projects"
                                filterOption={filterOptionLabel}
                                disabled={autoSelectAllJiraProjects === true}
                            >
                                {availableProjects.map((project) => (
                                    <Select.Option key={project.key} value={project.key} label={project.name}>
                                        {project.name} ({project.key})
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Auto-select/sync all projects',
                    initialValue: integration.data.autoSelectAllProjects,
                    name: 'autoSelectAllProjects',
                    fieldType: FieldType.Checkbox,
                    disabled: !provider.data.canOfferAutoSelectAllProjects,
                    helpMessage:
                        !provider.data.canOfferAutoSelectAllProjects ? 'Too many projects for auto-select/sync ' : null,
                    rules: [{ required: false }],
                },
                {
                    label: 'Additional done statuses',
                    initialValue: integration.data.additionalDoneStatuses,
                    data: provider.data.availableAdditionalDoneStatuses,
                    name: 'additionalDoneStatuses',
                    fieldType: FieldType.Custom,
                    rules: [{ required: !hasExistingDoneStatuses }],
                    helpMessage: 'Existing Jira-configured done statuses: '.concat(
                        hasExistingDoneStatuses ? provider.data.doneStatuses.join(', ') : '(none)',
                    ),

                    renderField: (availableAdditionalDoneStatuses: string[]) => {
                        return (
                            <Select allowClear mode="multiple" placeholder="Select additional done statuses">
                                {availableAdditionalDoneStatuses.map((doneStatus) => (
                                    <Select.Option key={doneStatus} value={doneStatus}>
                                        {doneStatus}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Additional fields',
                    initialValue: integration.data.customFields ?? [],
                    data: integration.data.customFields ?? [],
                    name: 'customFields',
                    fieldType: FieldType.Custom,
                    rules: [
                        { required: false },
                        {
                            validator: (_, values) => {
                                return ticketCustomFieldsValidatorFunction(values);
                            },
                        },
                    ],
                    helpMessage:
                        'Fields entered here will be available for filtering and visible in ticket details. Use newlines to enter multiple field names. Field names are case sensitive.',

                    renderField: (fieldNames: string[]) => {
                        return (
                            <Select
                                mode="tags"
                                allowClear
                                placeholder="Enter field names"
                                tokenSeparators={['\n']}
                                dropdownStyle={{ display: 'none' }}
                                style={{
                                    flex: 1,
                                }}
                            >
                                {fieldNames.map((fieldName) => (
                                    <Select.Option key={fieldName} value={fieldName}>
                                        {fieldName}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.Shortcut: {
            const provider = integrationProvider as IntegrationProviderShortcut;

            if (!provider) {
                return [];
            }

            const hasExistingDoneStatuses = provider.data.doneStatuses.length > 0;

            return [
                {
                    label: 'Teams',
                    initialValue: integration.data.selectedGroups.map((project) => project.id),
                    data: provider.data.availableGroups,
                    name: 'teams',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (availableGroups: ShortcutGroup[]) => {
                        return (
                            <Select
                                allowClear
                                mode="multiple"
                                placeholder="Select teams"
                                filterOption={filterOptionLabel}
                            >
                                {availableGroups.map((group) => (
                                    <Select.Option key={group.id} value={group.id} label={group.name}>
                                        {group.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Additional done statuses',
                    initialValue: integration.data.additionalDoneStatuses.map((state) => String(state.id)),
                    data: provider.data.availableAdditionalDoneStatuses,
                    name: 'additionalDoneStatuses',
                    fieldType: FieldType.Custom,
                    rules: [{ required: !hasExistingDoneStatuses }],
                    helpMessage: 'Existing Shortcut-configured done statuses: '.concat(
                        hasExistingDoneStatuses ?
                            provider.data.doneStatuses.map((state) => state.name).join(', ')
                        :   '(none)',
                    ),

                    renderField: (availableAdditionalDoneStatuses: ShortcutWorkflowState[]) => {
                        return (
                            <Select allowClear mode="multiple" placeholder="Select additional done statuses">
                                {availableAdditionalDoneStatuses.map((doneStatus) => (
                                    <Select.Option key={String(doneStatus.id)} value={String(doneStatus.id)}>
                                        {doneStatus.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.Pivotal: {
            const provider = integrationProvider as IntegrationProviderPivotal;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: 'Projects',
                    initialValue: integration.data.selectedProjects.map((project) => project.id),
                    data: provider.data.availableProjects,
                    name: 'projectIds',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (availableProjects: PivotalProject[]) => {
                        return (
                            <Select
                                allowClear
                                mode="multiple"
                                placeholder="Select projects"
                                filterOption={filterOptionLabel}
                            >
                                {availableProjects.map((project) => (
                                    <Select.Option key={project.id} value={project.id} label={project.name}>
                                        {project.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.Asana: {
            const provider = integrationProvider as IntegrationProviderAsana;
            if (!provider) {
                return [];
            }

            // Only fall back to free-text done sections when we genuinely have no sections to
            // offer. On large workspaces (projectOverflow) we now still fetch sections for the
            // selected projects, so prefer the real dropdown whenever sections are available.
            const hasAvailableSections = (provider.data.availableSections ?? []).length > 0;
            const useFreeTextDoneSections = provider.data.projectOverflow && !hasAvailableSections;

            return [
                {
                    label: 'Projects',
                    initialValue: [
                        ...(integration.data.selectedProjects ?? []).map((project) => project.gid),
                        ...(integration.data.selectedPatternedProjectNames ?? []),
                    ],
                    data: provider.data.availableProjects,
                    name: 'projects',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (availableProjects: AsanaProject[]) => {
                        return (
                            <Select
                                allowClear
                                mode="tags"
                                placeholder="Select projects"
                                filterOption={filterOptionLabel}
                            >
                                {availableProjects.map((project) => (
                                    <Select.Option key={project.gid} value={project.gid} label={project.name}>
                                        {project.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Done Sections',
                    initialValue: integration.data.additionalDoneSections ?? [],
                    name: 'additionalDoneStatuses',
                    fieldType: FieldType.Custom,
                    data: provider.data.availableSections,

                    renderField: (data: string[]) => {
                        if (useFreeTextDoneSections) {
                            // Multiple-select free text field
                            return <Select allowClear mode="multiple" placeholder="Specify additional done statuses" />;
                        } else {
                            return (
                                <Select mode="multiple" placeholder="Select" filterOption={filterOptionLabel}>
                                    {data.map((section) => {
                                        return (
                                            <Select.Option value={section} key={section} label={section}>
                                                {section}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            );
                        }
                    },
                    rules: [{ required: false }],
                    helpMessage: 'Select specific Sections that should be treated as "done"',
                },
            ];
        }
        case IntegrationType.Linear: {
            const provider = integrationProvider as IntegrationProviderLinear;

            if (!provider) {
                return [];
            }

            const hasExistingDoneStatuses = provider.data.doneStatuses.length > 0;

            return [
                {
                    label: 'Teams',
                    initialValue: integration.data.selectedTeams.map((team) => team.id),
                    data: provider.data.availableTeams,
                    name: 'teams',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (availableTeams: LinearTeam[]) => {
                        return (
                            <Select
                                allowClear
                                mode="multiple"
                                placeholder="Select teams"
                                filterOption={filterOptionLabel}
                            >
                                {availableTeams.map((team) => (
                                    <Select.Option key={team.id} value={team.id} label={team.name}>
                                        {team.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                    placeholder: 'Select teams',
                },
                {
                    label: 'Additional done statuses',
                    initialValue: integration.data.additionalDoneStatuses,
                    data: provider.data.availableAdditionalDoneStatuses,
                    name: 'additionalDoneStatuses',
                    fieldType: FieldType.Custom,
                    rules: [{ required: !hasExistingDoneStatuses }],
                    helpMessage: 'Existing Linear-configured done statuses: '.concat(
                        hasExistingDoneStatuses ? provider.data.doneStatuses.join(', ') : '(none)',
                    ),

                    renderField: (availableAdditionalDoneStatuses: string[]) => {
                        return (
                            <Select allowClear mode="multiple" placeholder="Select additional done statuses">
                                {availableAdditionalDoneStatuses.map((doneStatus) => (
                                    <Select.Option key={doneStatus} value={doneStatus}>
                                        {doneStatus}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                    placeholder: 'Select additional done statuses',
                },
            ];
        }
        case IntegrationType.Slack: {
            const provider = integrationProvider as IntegrationProviderSlack;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: 'Primary channel(s)',
                    data: provider.data?.availableChannels,
                    initialValue: integration.data?.primaryChannels?.map((chan) => chan.id),
                    name: 'primaryChannelIds',
                    fieldType: FieldType.Custom,
                    rules: [
                        { required: true },
                        {
                            validator: (_, values) => {
                                return commsChannelsValidatorFunction(provider.type, values);
                            },
                        },
                    ],
                    placeholder: 'Select',

                    renderField: (availableChannels: CommsChannel[]) => {
                        return (
                            <Select
                                mode="tags"
                                notFoundContent={null}
                                showSearch
                                allowClear
                                filterOption={filterOptionLabel}
                            >
                                {availableChannels.map((channel) => (
                                    <Select.Option key={channel.id} value={channel.id} label={channel.name}>
                                        {channel.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                    helpMessage: primaryChannelsHelpMessage,
                },
                getAppendNotificationSettingsField(integration),
            ];
        }
        case IntegrationType.CircleCI: {
            const provider = integrationProvider as IntegrationProviderCircleCI;

            if (!provider) {
                return [];
            }

            const repoHelperMessage =
                (platform === AppPlatform.ReactNativeOTA ?
                    'The name of the repo from which you deploy your code. '
                :   'The name of the repo from which Release Candidates are built. ') +
                "If your selected project type is 'circleci', enter your project ID instead of repo name.";

            const fields: FormFieldData[] = [
                {
                    label: 'Org name/Project type',
                    initialValue: integration.data?.collaboration?.id,
                    data:
                        provider.data?.availableCollaborations?.map((collab) => {
                            return {
                                value: collab.id,
                                displayValue: collab.orgName + '/' + collab.projectType,
                            };
                        }) ?? [],
                    name: 'collaborationId',
                    fieldType: FieldType.Select,
                    rules: [{ required: true }],
                },
                {
                    label: 'Repo name/Project ID',
                    initialValue: integration.data.repoName,
                    name: 'repoName',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    helpMessage: repoHelperMessage,
                },
                {
                    label: 'Pipeline definition ID',
                    initialValue: integration.data.definitionId,
                    name: 'definitionId',
                    rules: [],
                    fieldType: FieldType.Input,
                    helpMessage:
                        'If Runway should trigger a specific pipeline definition. This can be found in the page Project Settings > Pipelines.',
                },
                {
                    label: isWorkflowCIStepEnabled ? `Workflow` : `${rcBuildName} workflow`,
                    initialValue: integration.data.workflowName,
                    data: [],
                    name: 'workflowId',
                    fieldType: FieldType.Input,
                    rules: [{ required: true }],
                },
                workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Staging),
            ];

            if (!isWorkflowCIStepEnabled) {
                fields.push(
                    ...[
                        {
                            label: 'Working branch workflow',
                            initialValue: integration.data.devWorkflowId,
                            data: [],
                            name: 'devWorkflowId',
                            fieldType: FieldType.Input,
                            rules: [{ required: false }],
                            helpMessage: devWorkflowHelper,
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Working),
                        {
                            label: prodWorkflowLabel,
                            helpMessage: prodWorkflowHelperMessage,
                            initialValue: integration.data.prodWorkflowId,
                            data: [],
                            rules: [],
                            name: 'prodWorkflowId',
                            fieldType: FieldType.Input,
                        },
                        workflowArgumentFieldForWorkflowType(workflowArguments, WorkflowType.Production),
                    ],
                );
            }

            fields.push(
                ...strictWorkflowArgumentFilteringFields(
                    integration.data as BaseCIIntegrationData,
                    isWorkflowCIStepEnabled,
                ),
            );
            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.TestRail: {
            const provider = integrationProvider as IntegrationProviderTestRail;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: `Project`,
                    initialValue: integration.data?.selectedProject?.id,
                    data:
                        provider.data?.availableProjects?.map((project) => {
                            return {
                                value: project.id,
                                displayValue: project.name,
                            };
                        }) ?? [],
                    name: 'projectId',
                    fieldType: FieldType.Select,
                    rules: [{ required: true }],
                },
                {
                    fieldType: FieldType.WrappedGroup,
                    groupedFields: [
                        {
                            label: 'Test run naming keywords',
                            tooltip:
                                'Enter any additional keywords (e.g. "iOS" or "Android") that you use to associate test runs with this app (case insensitive). Runway already matches on version string to associate test runs with releases.',
                            initialValue: integration.data?.testRunNameKeywords ?? [],
                            data: integration.data?.testRunNameKeywords ?? [],
                            name: 'testRunNameKeywords',
                            fieldType: FieldType.Custom,
                            rules: [
                                { required: false },
                                {
                                    validator: (_, values) => {
                                        return testRunNamingKeywordsValidatorFunction(values);
                                    },
                                },
                            ],

                            renderField: (keywords: string[]) => {
                                return (
                                    <Select
                                        mode="tags"
                                        allowClear
                                        tokenSeparators={[' ', '\n']}
                                        dropdownStyle={{ display: 'none' }}
                                        style={{
                                            flex: 1,
                                        }}
                                    >
                                        {keywords.map((keyword) => (
                                            <Select.Option key={keyword} value={keyword}>
                                                {keyword}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                );
                            },
                        },
                        {
                            label: ' ',
                            name: 'testRunNameKeywordsMatchAny',
                            fieldType: FieldType.Custom,
                            initialValue: integration.data?.testRunNameKeywordsMatchAny ?? false,

                            renderField: () => {
                                return (
                                    <Select
                                        options={[
                                            {
                                                value: true,
                                                label: 'Match any',
                                            },
                                            {
                                                value: false,
                                                label: 'Match all',
                                            },
                                        ]}
                                    />
                                );
                            },
                        },
                    ],
                    groupChildrenWrapper: (children) => {
                        return (
                            <div
                                style={{
                                    display: 'grid',
                                    gap: 8,
                                    width: '100%',
                                    gridTemplateColumns: '3fr 1fr',
                                }}
                            >
                                {children}
                            </div>
                        );
                    },
                },
                testRunNamePatternField(integration.data?.testRunNamePattern),
            ];
        }
        case IntegrationType.BrowserStack: {
            const provider = integrationProvider as IntegrationProviderBrowserStack;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: `Project`,
                    initialValue: integration.data?.selectedProject?.identifier,
                    data:
                        provider.data?.availableProjects?.map((project) => {
                            return {
                                value: project.identifier,
                                displayValue: `${project.name} (${project.identifier})`,
                            };
                        }) ?? [],
                    name: 'projectId',
                    fieldType: FieldType.Select,
                    rules: [{ required: true }],
                },
                {
                    fieldType: FieldType.WrappedGroup,
                    groupedFields: [
                        {
                            label: 'Test run naming keywords',
                            tooltip:
                                'Enter any additional keywords (e.g. "iOS" or "Android") that you use to associate test runs with this app (case insensitive). Runway already matches on version string to associate test runs with releases.',
                            initialValue: integration.data?.testRunNameKeywords ?? [],
                            data: integration.data?.testRunNameKeywords ?? [],
                            name: 'testRunNameKeywords',
                            fieldType: FieldType.Custom,
                            rules: [
                                { required: false },
                                {
                                    validator: (_, values) => {
                                        return testRunNamingKeywordsValidatorFunction(values);
                                    },
                                },
                            ],

                            renderField: (keywords: string[]) => {
                                return (
                                    <Select
                                        mode="tags"
                                        allowClear
                                        tokenSeparators={[' ', '\n']}
                                        dropdownStyle={{ display: 'none' }}
                                        style={{
                                            flex: 1,
                                        }}
                                    >
                                        {keywords.map((keyword) => (
                                            <Select.Option key={keyword} value={keyword}>
                                                {keyword}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                );
                            },
                        },
                        {
                            label: ' ',
                            name: 'testRunNameKeywordsMatchAny',
                            fieldType: FieldType.Custom,
                            initialValue: integration.data?.testRunNameKeywordsMatchAny ?? false,

                            renderField: () => {
                                return (
                                    <Select
                                        options={[
                                            {
                                                value: true,
                                                label: 'Match any',
                                            },
                                            {
                                                value: false,
                                                label: 'Match all',
                                            },
                                        ]}
                                    />
                                );
                            },
                        },
                    ],
                    groupChildrenWrapper: (children) => {
                        return (
                            <div
                                style={{
                                    display: 'grid',
                                    gap: 8,
                                    width: '100%',
                                    gridTemplateColumns: '3fr 1fr',
                                }}
                            >
                                {children}
                            </div>
                        );
                    },
                },
                testRunNamePatternField(integration.data?.testRunNamePattern),
            ];
        }
        case IntegrationType.Qase: {
            const provider = integrationProvider as IntegrationProviderQase;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: `Project`,
                    initialValue: integration.data?.selectedProject?.code,
                    data:
                        provider.data?.availableProjects?.map((project) => {
                            return {
                                value: project.code,
                                displayValue: project.title + ' (' + project.code + ')',
                            };
                        }) ?? [],
                    name: 'projectId',
                    fieldType: FieldType.Select,
                    rules: [{ required: true }],
                },
                {
                    fieldType: FieldType.WrappedGroup,
                    groupedFields: [
                        {
                            label: 'Test run naming keywords',
                            tooltip:
                                'Enter any additional keywords (e.g. "iOS" or "Android") that you use to associate test runs with this app (case insensitive). Runway already matches on version string to associate test runs with releases.',
                            initialValue: integration.data?.testRunNameKeywords ?? [],
                            data: integration.data?.testRunNameKeywords ?? [],
                            name: 'testRunNameKeywords',
                            fieldType: FieldType.Custom,
                            rules: [
                                { required: false },
                                {
                                    validator: (_, values) => {
                                        return testRunNamingKeywordsValidatorFunction(values);
                                    },
                                },
                            ],

                            renderField: (keywords: string[]) => {
                                return (
                                    <Select
                                        mode="tags"
                                        allowClear
                                        tokenSeparators={[' ', '\n']}
                                        dropdownStyle={{ display: 'none' }}
                                        style={{
                                            flex: 1,
                                        }}
                                    >
                                        {keywords.map((keyword) => (
                                            <Select.Option key={keyword} value={keyword}>
                                                {keyword}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                );
                            },
                        },
                        {
                            label: ' ',
                            name: 'testRunNameKeywordsMatchAny',
                            fieldType: FieldType.Custom,
                            initialValue: integration.data?.testRunNameKeywordsMatchAny ?? false,

                            renderField: () => {
                                return (
                                    <Select
                                        options={[
                                            {
                                                value: true,
                                                label: 'Match any',
                                            },
                                            {
                                                value: false,
                                                label: 'Match all',
                                            },
                                        ]}
                                    />
                                );
                            },
                        },
                    ],
                    groupChildrenWrapper: (children) => {
                        return (
                            <div
                                style={{
                                    display: 'grid',
                                    gap: 8,
                                    width: '100%',
                                    gridTemplateColumns: '3fr 1fr',
                                }}
                            >
                                {children}
                            </div>
                        );
                    },
                },
                testRunNamePatternField(integration.data?.testRunNamePattern),
            ];
        }
        case IntegrationType.TravisCI: {
            const fields: FormFieldData[] = [
                {
                    label: 'Repo slug',
                    initialValue: integration.data.repoSlug,
                    name: 'repoName',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    helpMessage: 'Formatted as <repo owner>/<repo name>',
                },
            ];

            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }
        case IntegrationType.Firebase:
            return [
                {
                    label: 'Project ID',
                    name: 'projectId',
                    initialValue: integration.data.projectId,
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
                {
                    label: 'GA Property ID',
                    name: 'propertyId',
                    initialValue: integration.data.gaPropertyId,
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    helpMessage:
                        'The ID you enter here should correspond to the Google Analytics Property that your app sends data to ',
                },
            ];
        case IntegrationType.Xray: {
            return [
                {
                    fieldType: FieldType.WrappedGroup,
                    groupedFields: [
                        {
                            label: 'Test execution naming keywords',
                            tooltip:
                                'Enter any additional keywords (e.g. "iOS" or "Android") that you use to associate test executions with this app (case insensitive). Runway already matches on version string to associate test executions with releases.',
                            initialValue: integration.data?.testRunNameKeywords ?? [],
                            data: integration.data?.testRunNameKeywords ?? [],
                            name: 'testRunNameKeywords',
                            fieldType: FieldType.Custom,
                            rules: [
                                { required: false },
                                {
                                    validator: (_, values) => {
                                        return testRunNamingKeywordsValidatorFunction(values);
                                    },
                                },
                            ],

                            renderField: (keywords: string[]) => {
                                return (
                                    <Select
                                        mode="tags"
                                        allowClear
                                        tokenSeparators={[' ', '\n']}
                                        dropdownStyle={{ display: 'none' }}
                                    >
                                        {keywords.map((keyword) => (
                                            <Select.Option key={keyword} value={keyword}>
                                                {keyword}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                );
                            },
                        },
                        {
                            label: ' ',
                            name: 'testRunNameKeywordsMatchAny',
                            fieldType: FieldType.Custom,
                            initialValue: integration.data?.testRunNameKeywordsMatchAny ?? false,

                            renderField: () => {
                                return (
                                    <Select
                                        options={[
                                            {
                                                value: true,
                                                label: 'Match any',
                                            },
                                            {
                                                value: false,
                                                label: 'Match all',
                                            },
                                        ]}
                                    />
                                );
                            },
                        },
                    ],
                    groupChildrenWrapper: (children) => {
                        return (
                            <div
                                style={{
                                    display: 'grid',
                                    gap: 8,
                                    width: '100%',
                                    gridTemplateColumns: '3fr 1fr',
                                }}
                            >
                                {children}
                            </div>
                        );
                    },
                },
                testRunNamePatternField(integration.data?.testRunNamePattern),
            ];
        }
        case IntegrationType.Amplitude: {
            const provider = integrationProvider as IntegrationProviderAmplitude;

            if (!provider) {
                return [];
            }

            const baseEventsFormField = baseEventsToMonitorFormField(
                integration.id,
                integration.data?.selectedEventsAndParams,
            );

            return [
                {
                    ...baseEventsFormField,
                    data: provider.data.allEventTypes,

                    renderField: (allEventTypes: string[]) => {
                        const allPossibleEventsAndParams: Map<string, AnalyticsProperty[]> = allEventTypes?.reduce(
                            (acc, event) => {
                                acc.set(event, []); // Deliberately not setting any params for Amplitude
                                return acc;
                            },
                            new Map(),
                        );
                        return (
                            <EventsAndParamsInput
                                integrationId={integration.id}
                                eventsAndParams={allPossibleEventsAndParams}
                            />
                        );
                    },
                },
            ];
        }

        case IntegrationType.Optimizely: {
            const provider = integrationProvider as IntegrationProviderOptimizely;

            if (!provider) {
                return [];
            }

            const projectId = optimizelyProjectId ?? integration.data?.selectedProject?.id;

            const availableEnvironments = (projectId && provider.data.environments[projectId]) ?? [];

            return [
                {
                    label: 'Project',
                    initialValue: integration.data?.selectedProject?.id ?? null,
                    name: 'projectId',
                    fieldType: FieldType.Custom,
                    placeholder: 'Select',
                    data: provider.data?.projects ?? [],
                    renderField: (projects: OptimizelyProject[]) => {
                        return (
                            <Select onChange={onChangeOptimizelyProjectId}>
                                {projects.map((project) => {
                                    return (
                                        <Select.Option value={project.id} key={project.id} label={project.name}>
                                            {project.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                },
                {
                    label: 'Production environment',
                    initialValue: integration.data?.selectedEnvironment?.id ?? null,
                    name: 'environmentId',
                    fieldType: FieldType.Custom,
                    placeholder: 'Select',
                    data: availableEnvironments,
                    renderField: (environments: OptimizelyEnvironment[]) => {
                        return (
                            <Select>
                                {environments.map((env) => {
                                    return (
                                        <Select.Option value={env.id} key={env.id} label={env.name}>
                                            {env.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                    extraFormItemProps: { dependencies: ['projectId'] },
                },
                {
                    label: 'Release version attribute name',
                    helpMessage: 'Enter the name of the attribute you use to segment feature flags by release version',
                    placeholder: 'appVersion',
                    fieldType: FieldType.Input,
                    name: 'versionAttributeName',
                    initialValue: integration.data?.selectedVersionAttributeName ?? null,
                    rules: [{ required: true }],
                },
                {
                    label: 'Audience/platform',
                    helpMessage:
                        'Filter ingested flags by platform (labels come from Statsig tags and platform/OS rules). Case-insensitive. Leave blank to include all flags.',
                    fieldType: FieldType.Custom,
                    name: 'featureFlagPlatformFilter',
                    initialValue: featureFlagPlatformFilterChipsFromStored(
                        integration.data?.selectedFeatureFlagPlatformFilter,
                    ),
                    renderField: () => featureFlagPlatformFilterTagsSelect('Select or type in platform terms'),
                },
            ];
        }
        case IntegrationType.GoogleAnalytics: {
            const provider = integrationProvider as IntegrationProviderGoogle;
            if (!provider) {
                return [];
            }

            const baseEventsFormField = baseEventsToMonitorFormField(
                integration.id,
                integration.data?.selectedEventsAndParams,
            );
            return [
                {
                    ...baseEventsFormField,
                    data: provider.data.analyticsResources,

                    renderField: (resources: GoogleAnalyticsResources) => {
                        const events = resources?.allEventNames;
                        const allPossibleEventsAndParams: Map<string, AnalyticsProperty[]> = events?.reduce(
                            (acc, event) => {
                                acc.set(
                                    event,
                                    resources.allMetrics?.map((m) => {
                                        return {
                                            name: m.name,
                                            id: m.apiName,
                                        };
                                    }),
                                );
                                return acc;
                            },
                            new Map(),
                        );
                        return (
                            <EventsAndParamsInput
                                integrationId={integration.id}
                                eventsAndParams={allPossibleEventsAndParams}
                            />
                        );
                    },
                },
                {
                    label: 'Data stream',
                    initialValue: integration.data?.dataStreamId ?? null,
                    name: 'dataStreamId',
                    fieldType: FieldType.Custom,
                    placeholder: 'Select',
                    data: provider.data.analyticsResources.dataStreams,
                    renderField: (dataStreams: GoogleAnalyticsDataStream[]) => {
                        return (
                            <Select>
                                {dataStreams.map((dataStream) => {
                                    let dsText = dataStream.id;
                                    if (dataStream.name) {
                                        dsText += ` (${dataStream.name})`;
                                    } else if (dataStream.platform) {
                                        dsText += ` (${dataStream.platform})`;
                                    }
                                    return (
                                        <Select.Option value={dataStream.id} key={dataStream.id} label={dataStream.id}>
                                            {dsText}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                },
            ];
        }

        case IntegrationType.AndroidVitals: {
            const provider = integrationProvider as IntegrationProviderGoogle;
            if (!provider) {
                return [];
            }

            // Define available metrics
            const androidVitalsMetrics = provider.data.androidVitalsResources.allMetrics;
            const androidVitalsApps = provider.data.androidVitalsResources.allApps;

            return [
                {
                    label: 'App',
                    initialValue: integration.data?.appId ?? null,
                    name: 'selectedApp',
                    fieldType: FieldType.Custom,
                    data: androidVitalsApps,
                    renderField: (apps) => {
                        return (
                            <Select placeholder="Select app">
                                {apps?.map((app) => (
                                    <Select.Option key={app.name} value={app.name}>
                                        {app.displayName}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                    rules: [{ required: true, message: 'Please select an App' }],
                },
                {
                    label: 'Metrics',
                    initialValue: integration.data?.selectedEventsAndParams?.map((e) => e.eventName) ?? [],
                    name: 'rawSelectedMetrics',
                    fieldType: FieldType.Custom,
                    helpMessage: 'Slow render rate is only available for games.',
                    data: androidVitalsMetrics,
                    renderField: (metrics) => {
                        return (
                            <Select mode="multiple" placeholder="Select metrics">
                                {metrics?.map((metric) => (
                                    <Select.Option key={metric} value={metric}>
                                        {metric}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                    rules: [{ required: true, message: 'Please select at least one metric' }],
                },
            ];
        }

        case IntegrationType.ApplePowerPerformanceMetrics: {
            const provider = integrationProvider as IntegrationProviderApple;
            if (!provider) {
                return [];
            }

            const availableMetrics = provider.data.availablePowerAndPerformanceMetrics;

            return [
                {
                    label: 'Metrics',
                    initialValue: integration.data?.selectedEventsAndParams?.map((e) => e.eventName) ?? [],
                    name: 'rawSelectedMetrics',
                    fieldType: FieldType.Custom,
                    data: availableMetrics,
                    renderField: (metrics) => {
                        return (
                            <Select mode="multiple" placeholder="Select metrics">
                                {metrics?.map((metric) => (
                                    <Select.Option key={metric} value={metric}>
                                        {metric}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                    rules: [{ required: true, message: 'Please select at least one metric' }],
                },
            ];
        }

        case IntegrationType.LaunchDarkly: {
            const provider = integrationProvider as IntegrationProviderLaunchDarkly;

            if (!provider) {
                return [];
            }

            const projectId = launchDarklyProjectId ?? integration.data?.selectedProject?.key;

            const availableEnvironments = projectId ? (provider.data?.environments[projectId] ?? []) : [];

            return [
                {
                    label: 'Project',
                    initialValue: integration.data?.selectedProject?.key ?? null,
                    name: 'projectId',
                    fieldType: FieldType.Custom,
                    placeholder: 'Select',
                    data: provider.data?.projects ?? [],
                    renderField: (projects: LaunchDarklyProject[]) => {
                        return (
                            <Select onChange={onChangeLaunchDarklyProjectId}>
                                {projects.map((project) => {
                                    return (
                                        <Select.Option value={project.key} key={project.key} label={project.name}>
                                            {project.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                },
                {
                    label: 'Production environment',
                    initialValue: integration.data?.selectedEnvironment?.id ?? null,
                    name: 'environmentId',
                    fieldType: FieldType.Custom,
                    placeholder: 'Select',
                    data: availableEnvironments,
                    renderField: (environments: LaunchDarklyEnvironment[]) => {
                        return (
                            <Select>
                                {environments.map((env) => {
                                    return (
                                        <Select.Option value={env.id} key={env.id} label={env.name}>
                                            {env.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                    extraFormItemProps: { dependencies: ['projectId'] },
                },
                {
                    fieldType: FieldType.WrappedGroup,
                    helpMessage:
                        'Filter fetched feature flags by tags. Leave blank to fetch all feature flags for the project & environment',
                    groupedFields: [
                        {
                            label: 'Tags',
                            initialValue: integration.data?.selectedTags ?? [],
                            name: 'tags',
                            fieldType: FieldType.Custom,
                            data: provider.data?.tags ?? [],
                            renderField: (tags: string[]) => {
                                return (
                                    <Select mode="multiple" filterOption={filterOptionLabel}>
                                        {tags.map((tag) => {
                                            return (
                                                <Select.Option value={tag} key={tag} label={tag}>
                                                    {tag}
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                );
                            },
                        },
                        {
                            label: ' ',
                            name: 'tagsMatchAny',
                            fieldType: FieldType.Custom,
                            initialValue: integration.data?.selectedTagsMatchAny ?? false,

                            renderField: () => {
                                return (
                                    <Select
                                        options={[
                                            {
                                                value: true,
                                                label: 'Match any',
                                            },
                                            {
                                                value: false,
                                                label: 'Match all',
                                            },
                                        ]}
                                    />
                                );
                            },
                        },
                    ],
                    groupChildrenWrapper: (children) => {
                        return (
                            <div
                                style={{
                                    display: 'grid',
                                    gap: 8,
                                    width: '100%',
                                    gridTemplateColumns: '3fr 1fr',
                                }}
                            >
                                {children}
                            </div>
                        );
                    },
                },
                {
                    label: 'Release version attribute name',
                    helpMessage: 'Enter the name of the attribute you use to segment feature flags by release version',
                    placeholder: 'appVersion',
                    fieldType: FieldType.Input,
                    name: 'versionAttributeName',
                    initialValue: integration.data?.selectedVersionAttributeName ?? null,
                    rules: [{ required: true }],
                },
            ];
        }

        case IntegrationType.ExpoEAS: {
            const provider = integrationProvider as IntegrationProviderExpoEAS;
            if (!provider) {
                return [];
            }
            return [
                {
                    label: 'Expo project',
                    initialValue: integration.data?.projectId ?? null,
                    name: 'projectId',
                    fieldType: FieldType.Custom,
                    data: provider.data?.projects ?? [],
                    renderField: (projects: ExpoEASProject[]) => (
                        <Select showSearch filterOption={filterOptionLabel}>
                            {projects.map((project) => (
                                <Select.Option
                                    value={project.id}
                                    key={project.id}
                                    label={project.fullName || project.name}
                                >
                                    {project.fullName || project.name}
                                </Select.Option>
                            ))}
                        </Select>
                    ),
                    rules: [{ required: true }],
                },
                {
                    label: 'Production channel',
                    initialValue: integration.data?.channel ?? null,
                    name: 'channel',
                    fieldType: FieldType.Custom,
                    data: provider.data?.channels ?? {},
                    renderField: (channelsByProjectId: Record<string, ExpoEASChannel[]>) => (
                        <ExpoEASChannelSelect channelsByProjectId={channelsByProjectId} />
                    ),
                    rules: [{ required: true }],
                },
            ];
        }

        case IntegrationType.Statsig: {
            const provider = integrationProvider as IntegrationProviderStatsig;

            if (!provider) {
                return [];
            }

            const providerData = provider.data ?? { environments: [], tags: [] };
            const environments = providerData.environments ?? [];

            const fields: FormFieldData[] = [];

            // Add environment selection (required)
            fields.push({
                label: 'Production environment',
                initialValue: integration.data?.selectedEnvironment?.name ?? null,
                name: 'environmentId',
                fieldType: FieldType.Custom,
                placeholder: 'Select',
                data: environments,
                renderField: (environments: StatsigEnvironment[]) => {
                    return (
                        <Select>
                            {environments.map((env) => {
                                return (
                                    <Select.Option value={env.name} key={env.name} label={env.name}>
                                        {env.name}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    );
                },
                rules: [{ required: true }],
            });

            // Add version attribute name
            fields.push({
                label: (
                    <>
                        Release version attribute name
                        <Tooltip title="Enter the name of the attribute you use to segment feature flags by release version (e.g., app_version). If left blank, we won't be able to show which app versions your feature flags apply to.">
                            <InfoCircleOutlined style={{ marginLeft: 6, color: antGrey.grey6 }} />
                        </Tooltip>
                    </>
                ),
                placeholder: 'app_version',
                fieldType: FieldType.Input,
                name: 'versionAttributeName',
                initialValue: integration.data?.selectedVersionAttributeName ?? null,
            });

            // Add tags selection
            fields.push({
                fieldType: FieldType.WrappedGroup,
                groupedFields: [
                    {
                        label: (
                            <>
                                Tags
                                <Tooltip title="Filter fetched feature flags by tags. Leave blank to fetch all feature flags.">
                                    <InfoCircleOutlined style={{ marginLeft: 6, color: antGrey.grey6 }} />
                                </Tooltip>
                            </>
                        ),
                        initialValue: integration.data?.selectedTags ?? [],
                        name: 'tags',
                        fieldType: FieldType.Custom,
                        data: providerData.tags ?? [],
                        renderField: (tags: string[]) => {
                            return (
                                <Select mode="multiple" placeholder="All" filterOption={filterOptionLabel}>
                                    {tags.map((tag) => {
                                        return (
                                            <Select.Option value={tag} key={tag} label={tag}>
                                                {tag}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            );
                        },
                    },
                    {
                        label: ' ',
                        name: 'tagsMatchAny',
                        fieldType: FieldType.Custom,
                        initialValue: integration.data?.selectedTagsMatchAny ?? false,
                        renderField: () => {
                            return (
                                <Select
                                    options={[
                                        {
                                            value: true,
                                            label: 'Match any',
                                        },
                                        {
                                            value: false,
                                            label: 'Match all',
                                        },
                                    ]}
                                />
                            );
                        },
                    },
                ],
                groupChildrenWrapper: (children) => {
                    return (
                        <div
                            style={{
                                display: 'grid',
                                gap: 8,
                                width: '100%',
                                gridTemplateColumns: '3fr 1fr',
                            }}
                        >
                            {children}
                        </div>
                    );
                },
            });

            const selectedFilterSource = getDefaultStatsigFilterSource(integration.data);
            fields.push({
                fieldType: FieldType.WrappedGroup,
                groupedFields: [
                    {
                        label: (
                            <>
                                Filter
                                <Tooltip title="Filter feature flags based on OS rules or name. Rule based filtering uses the OS rule criterion for gates/dynamic configs.">
                                    <InfoCircleOutlined style={{ marginLeft: 6, color: antGrey.grey6 }} />
                                </Tooltip>
                            </>
                        ),
                        fieldType: FieldType.Custom,
                        name: 'featureFlagPlatformFilterSource',
                        initialValue: selectedFilterSource,
                        renderField: () => <StatsigPlatformFilterSourceField />,
                        extraFormItemProps: { hasFeedback: false },
                    },
                    {
                        label: 'Match',
                        fieldType: FieldType.Custom,
                        name: 'featureFlagPlatformFilterMatchType',
                        initialValue:
                            selectedFilterSource === 'none' ? null
                            : selectedFilterSource === 'rules' ? 'equals'
                            : (integration.data?.selectedFeatureFlagPlatformFilterMatchType ?? 'contains'),
                        renderField: () => <StatsigPlatformFilterMatchTypeField />,
                        extraFormItemProps: { hasFeedback: false },
                    },
                    {
                        label: 'Value',
                        fieldType: FieldType.Custom,
                        name: 'featureFlagPlatformFilter',
                        initialValue:
                            selectedFilterSource === 'none' ? null
                            : selectedFilterSource === 'rules' ?
                                featureFlagPlatformFilterChipsFromStored(
                                    integration.data?.selectedFeatureFlagPlatformFilter,
                                )
                            :   (integration.data?.selectedFeatureFlagPlatformFilter ?? null),
                        renderField: () => <StatsigPlatformFilterValueField />,
                        extraFormItemProps: { hasFeedback: false },
                    },
                ],
                groupChildrenWrapper: (_children, groupedFields, renderFields) => {
                    return (
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const source = getFieldValue('featureFlagPlatformFilterSource') ?? 'rules';
                                const visibleFields =
                                    source === 'none' ?
                                        groupedFields.filter((f) => f.name === 'featureFlagPlatformFilterSource')
                                    :   groupedFields;
                                return (
                                    <div
                                        style={{
                                            display: 'grid',
                                            gap: 8,
                                            width: '100%',
                                            gridTemplateColumns: source === 'none' ? '1fr' : '1fr 1fr 2fr',
                                        }}
                                    >
                                        {renderFields(visibleFields)}
                                    </div>
                                );
                            }}
                        </Form.Item>
                    );
                },
            });

            return fields;
        }

        case IntegrationType.DatadogAnalytics: {
            const provider = integrationProvider as IntegrationProviderDatadog;

            if (!provider) {
                return [];
            }

            return [
                {
                    fieldType: FieldType.Custom,
                    skipFormItemWrapper: true,
                    renderField: () => (
                        <DatadogObservabilityAndAnalyticsForm
                            integration={integration as IntegrationDatadogAnalytics}
                            provider={provider}
                            platform={platform}
                            appId={appId}
                        />
                    ),
                },
            ];
        }

        case IntegrationType.MixpanelAnalytics: {
            const provider = integrationProvider as IntegrationProviderMixpanel;

            if (!provider) {
                return [];
            }

            const baseEventsFormField = baseEventsToMonitorFormField(
                integration.id,
                integration.data?.selectedEventsAndParams,
            );

            const customConfig = integration.data?.customSessionsConfiguration;
            const hasCustomSessionsConfig = !!customConfig;

            return [
                {
                    label: 'Project id',
                    initialValue: integration.data.projectId ?? null,
                    name: 'mixpanelProjectId',
                    fieldType: FieldType.Input,
                    rules: [
                        { required: true },
                        {
                            validator: (_, value) => {
                                const mixpanelProjectId = parseInt(value);
                                if (Number.isNaN(mixpanelProjectId)) {
                                    return Promise.reject('Mixpanel project id must be an integer');
                                }
                                return Promise.resolve();
                            },
                        },
                    ],
                },
                {
                    ...baseEventsFormField,

                    renderField: () => {
                        return <EventsAndParamsInput integrationId={integration.id} eventsAndParams={undefined} />;
                    },
                },
                {
                    initialValue: hasCustomSessionsConfig,
                    name: 'useCustomSessionsConfiguration',
                    fieldType: FieldType.Checkbox,
                    data: 'Use custom event for sessions computation',
                    rules: [{ required: false }],
                },
                {
                    fieldType: FieldType.Custom,
                    skipFormItemWrapper: true,
                    renderField: () => (
                        <Form.Item shouldUpdate noStyle>
                            {(form) => {
                                const useCustomConfig = form.getFieldValue('useCustomSessionsConfiguration');

                                if (!useCustomConfig) {
                                    return null;
                                }

                                return (
                                    <>
                                        <Form.Item
                                            label="Event name"
                                            name="customSessionsEventName"
                                            rules={[{ required: true, message: 'Event name is required' }]}
                                            initialValue={customConfig?.eventName ?? null}
                                        >
                                            <Input placeholder="Event name" />
                                        </Form.Item>
                                        <Form.Item
                                            label="Property name"
                                            name="customSessionsPropertyName"
                                            rules={[{ required: true, message: 'Property name is required' }]}
                                            initialValue={customConfig?.filteredParameter?.name ?? null}
                                        >
                                            <Input placeholder="Property name" />
                                        </Form.Item>
                                        <Form.Item
                                            label="Property value"
                                            name="customSessionsPropertyValue"
                                            rules={[{ required: true, message: 'Property value is required' }]}
                                            initialValue={customConfig?.filteredParameter?.filterValue ?? null}
                                        >
                                            <Input placeholder="Property value" />
                                        </Form.Item>
                                        <Form.Item
                                            label="Operator"
                                            name="customSessionsOperator"
                                            rules={[{ required: true, message: 'Operator is required' }]}
                                            initialValue={customConfig?.filteredParameter?.filterOperator ?? null}
                                        >
                                            <Select placeholder="Select operator">
                                                <Select.Option value={OBAAFilterOperator.Equal}>
                                                    {OBAAFilterOperator.Equal}
                                                </Select.Option>
                                                <Select.Option value={OBAAFilterOperator.LessThan}>
                                                    {OBAAFilterOperator.LessThan}
                                                </Select.Option>
                                                <Select.Option value={OBAAFilterOperator.GreaterThan}>
                                                    {OBAAFilterOperator.GreaterThan}
                                                </Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </>
                                );
                            }}
                        </Form.Item>
                    ),
                },
            ];
        }

        case IntegrationType.XcodeCloud: {
            const provider = integrationProvider as IntegrationProviderApple;

            if (!provider) {
                return [];
            }

            const fields: FormFieldData[] = [
                {
                    label: isWorkflowCIStepEnabled ? `Workflow` : `${rcBuildName} workflow`,
                    initialValue:
                        integration.data?.workflowId ?
                            [integration.data?.productId, integration.data?.workflowId]
                        :   null,
                    data: provider.data?.availableProductsAndWorkflows ?? [],
                    name: 'productIdAndWorkflowId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (productsAndWorkflows: ASCProductAndWorkflows[], extraProperties) => {
                        const cascaderOptions = productsAndWorkflows.map((productAndWorkflows) => {
                            return {
                                value: productAndWorkflows.product.id,
                                label: productAndWorkflows.product.name,
                                disabled: !productAndWorkflows.workflows || productAndWorkflows.workflows?.length === 0,
                                children:
                                    productAndWorkflows.workflows?.map((workflow) => {
                                        return {
                                            value: workflow.id,
                                            label: labelForASCWorkflow(workflow),
                                            children: null,
                                        };
                                    }) ?? null,
                            };
                        });
                        return (
                            <Cascader
                                allowClear
                                options={cascaderOptions}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                {
                    label: `Working branch workflow`,
                    initialValue:
                        integration.data?.devWorkflowId ?
                            [integration.data?.devProductId, integration.data?.devWorkflowId]
                        :   null,
                    data: provider.data?.availableProductsAndWorkflows ?? [],
                    name: 'devProductIdAndWorkflowId',
                    fieldType: FieldType.Custom,
                    helpMessage: devWorkflowHelper,
                    rules: [{ required: false }],

                    renderField: (productsAndWorkflows: ASCProductAndWorkflows[], extraProperties) => {
                        const cascaderOptions = productsAndWorkflows.map((productAndWorkflows) => {
                            return {
                                value: productAndWorkflows.product.id,
                                label: productAndWorkflows.product.name,
                                disabled: !productAndWorkflows.workflows || productAndWorkflows.workflows?.length === 0,
                                children:
                                    productAndWorkflows.workflows?.map((workflow) => {
                                        return {
                                            value: workflow.id,
                                            label: labelForASCWorkflow(workflow),
                                            children: null,
                                        };
                                    }) ?? null,
                            };
                        });
                        return (
                            <Cascader
                                allowClear
                                options={cascaderOptions}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                },
                {
                    label: prodWorkflowLabel,
                    helpMessage: prodWorkflowHelperMessage,
                    initialValue:
                        integration.data?.prodWorkflowId ?
                            [integration.data?.prodProductId, integration.data?.prodWorkflowId]
                        :   null,
                    data: provider.data?.availableProductsAndWorkflows ?? [],
                    name: 'prodProductIdAndWorkflowId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: false }],

                    renderField: (productsAndWorkflows: ASCProductAndWorkflows[], extraProperties) => {
                        const cascaderOptions = productsAndWorkflows.map((productAndWorkflows) => {
                            return {
                                value: productAndWorkflows.product.id,
                                label: productAndWorkflows.product.name,
                                disabled: !productAndWorkflows.workflows || productAndWorkflows.workflows?.length === 0,
                                children:
                                    productAndWorkflows.workflows?.map((workflow) => {
                                        return {
                                            value: workflow.id,
                                            label: labelForASCWorkflow(workflow),
                                            children: null,
                                        };
                                    }) ?? null,
                            };
                        });
                        return (
                            <Cascader
                                allowClear
                                options={cascaderOptions}
                                expandTrigger="hover"
                                placeholder={'Select'}
                                {...extraProperties}
                            />
                        );
                    },
                    placeholder: 'Select',
                },
            ];

            if (isGooglePlayStore) {
                fields.push(multiAPKField(integration.data));
            }

            return fields;
        }

        case IntegrationType.Monday: {
            const provider = integrationProvider as IntegrationProviderMonday;
            const boardColumnOptions = provider.data?.availableBoards.map((board) => {
                return {
                    label: board.name,
                    value: board.id,
                    children: board.columns.map((column) => {
                        return {
                            label: column.name,
                            value: column.id,
                        };
                    }),
                };
            });
            return [
                {
                    label: 'Boards',
                    initialValue: integration.data.selectedBoards?.map((board) => board.id),
                    data: provider.data?.availableBoards ?? [],
                    name: 'boardIds',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (availableBoards: MondayBoard[]) => {
                        return (
                            <Select allowClear mode="multiple" placeholder="Select boards">
                                {availableBoards.map((board) => (
                                    <Select.Option key={board.id} value={board.id} label={board.name}>
                                        {board.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Feature affiliation columns',
                    initialValue:
                        integration.data?.faColumns?.map((boardColumnPair) => [
                            boardColumnPair.boardId,
                            boardColumnPair.columnId,
                        ]) ?? [],
                    data: provider.data?.availableBoards ?? [],
                    name: 'faColumns',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],
                    helpMessage:
                        'For each selected board, choose the column that should be used to associate work items to releases in Runway',
                    renderField: () => {
                        return (
                            <Cascader
                                placeholder={'Select'}
                                multiple
                                showSearch
                                allowClear
                                options={boardColumnOptions}
                                expandTrigger="hover"
                                displayRender={(label) => {
                                    const boardName = label[0];
                                    const columnName = label[1];
                                    return (
                                        <Text>
                                            {columnName} <Text type="secondary">({boardName})</Text>
                                        </Text>
                                    );
                                }}
                            ></Cascader>
                        );
                    },
                },
                {
                    label: 'Done columns',
                    initialValue:
                        integration.data?.doneColumns?.map((boardColumnPair) => [
                            boardColumnPair.boardId,
                            boardColumnPair.columnId,
                        ]) ?? [],
                    data: provider.data?.availableBoards ?? [],
                    name: 'doneColumns',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],
                    renderField: () => {
                        return (
                            <Cascader
                                placeholder={'Select'}
                                multiple
                                showSearch
                                allowClear
                                options={boardColumnOptions}
                                expandTrigger="hover"
                                displayRender={(label) => {
                                    const boardName = label[0];
                                    const columnName = label[1];
                                    return (
                                        <Text>
                                            {columnName} <Text type="secondary">({boardName})</Text>
                                        </Text>
                                    );
                                }}
                            ></Cascader>
                        );
                    },
                },
                {
                    label: 'Additional Done statuses',
                    initialValue: integration.data?.additionalDoneStatuses ?? [],
                    name: 'additionalDoneStatuses',
                    fieldType: FieldType.Custom,
                    rules: [],
                    helpMessage:
                        "Specify the statuses that should be treated as 'done'. By default Runway will consider items with the 'Done' status only",
                    renderField: () => {
                        return (
                            <Select
                                mode="tags"
                                allowClear
                                dropdownStyle={{ display: 'none' }}
                                placeholder="e.g. 'complete'"
                            ></Select>
                        );
                    },
                },
            ];
        }

        case IntegrationType.RunwayBuildDistroBeta: {
            const provider = integrationProvider as IntegrationProviderRunwayBuildDistro;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: 'Bucket',
                    initialValue: integration.data?.selectedBucket?.id,
                    data: provider.data?.availableAppsAndBuckets[appId] ?? [],
                    name: 'bucketId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],
                    helpMessage: 'Choose the bucket that contains CI builds for beta testing.',

                    renderField: (availableBuckets: RunwayBuildDistroBucketInfo[]) => {
                        return (
                            <Select placeholder="Select bucket">
                                {availableBuckets.map((bucket) => (
                                    <Select.Option key={bucket.id} value={bucket.id} label={bucket.name}>
                                        {bucket.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.EmbraceStabilityMonitoring: {
            const provider = integrationProvider as IntegrationProviderEmbrace;

            if (!provider) {
                return [];
            }

            const appDataForPlatform = provider.data?.allApps.reduce(
                (result: { value: string; label: string }[], embraceApp) => {
                    if (embraceApp.platform === platform) {
                        return result.concat({
                            value: embraceApp.id,
                            label: `${embraceApp.id} - ${embraceApp.name} (${embraceApp.environment})`,
                        });
                    }
                    return result;
                },
                [],
            );

            return [
                getFormFieldForEmbraceIntegrationForm(
                    appDataForPlatform,
                    `Prod app`,
                    'prodAppSlug',
                    true,
                    integration.data?.selectedProdApp,
                ),
                getFormFieldForEmbraceIntegrationForm(
                    appDataForPlatform,
                    `Beta app`,
                    'devAppSlug',
                    false,
                    integration.data?.selectedBetaApp,
                ),
            ];
        }

        case IntegrationType.AzureVCS: {
            const provider = integrationProvider as IntegrationProviderAzure;

            if (!provider) {
                return [];
            }

            const repos =
                azureProjectId ?
                    provider.data?.availableProjectsAndResources?.find((info) => info.project.id === azureProjectId)
                        ?.repos
                :   [];

            return [
                {
                    label: 'Project',
                    initialValue: integration.data?.selectedProject?.id,
                    data: provider.data?.availableProjectsAndResources ?? [],
                    name: 'projectId',
                    fieldType: FieldType.Custom,

                    renderField: (data: AzureProjectAndResources[]) => {
                        return (
                            <Select onChange={onChangeAzureProjectId}>
                                {data.map((info) => {
                                    return (
                                        <Select.Option value={info.project.id} key={info.project.id}>
                                            {info.project.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                },
                {
                    label: 'Repos',
                    initialValue: integration.data?.selectedRepo?.id ?? '',
                    name: 'repoName',
                    fieldType: FieldType.Custom,
                    data: repos,

                    renderField: (data: AzureRepo[]) => {
                        return (
                            <Select>
                                {data?.map((repo) => {
                                    return (
                                        <Select.Option value={repo.id} key={repo.name}>
                                            {repo.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                },
            ];
        }

        case IntegrationType.MicrosoftTeams: {
            const provider = integrationProvider as IntegrationProviderAzureActiveDirectory;

            if (!provider) {
                return [];
            }

            const accessibleTeamIds = provider.data?.accessibleTeams ?? [];
            return [
                {
                    label: 'Primary channel(s)',
                    data: provider.data?.availableChannels,
                    initialValue: integration.data?.primaryChannels?.map((chan) => chan.id),
                    name: 'primaryChannelIds',
                    fieldType: FieldType.Custom,
                    rules: [
                        { required: true },
                        {
                            validator: (_, values) => {
                                return commsChannelsValidatorFunction(provider.type, values);
                            },
                        },
                        ...(accessibleTeamIds.length > 0 ?
                            [
                                {
                                    validator: (_: unknown, value: string[] | undefined) => {
                                        const selectedIds = value ?? [];
                                        const hasInaccessible = selectedIds.some((channelId: string) => {
                                            const parts = channelId.split('#runway#');
                                            const teamId = parts.length === 2 ? parts[0] : null;
                                            return teamId != null && !accessibleTeamIds.includes(teamId);
                                        });
                                        if (hasInaccessible) {
                                            return Promise.reject(
                                                'One or more selected channels are in teams the authenticated user is not a member of. Messages may fail with InsufficientPrivileges. Add the user to the team in Microsoft Teams, or select channels from teams they belong to.',
                                            );
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]
                        :   []),
                    ],
                    placeholder: 'Select',

                    renderField: (availableChannels: MicrosoftTeamsChannel[]) => {
                        return (
                            <TeamsChannelOverrideSelect
                                allChannels={availableChannels ?? []}
                                selectedChannels={integration.data?.primaryChannels ?? []}
                                teams={provider.data?.availableTeams ?? []}
                                disabled={false}
                            />
                        );
                    },
                    helpMessage: primaryChannelsHelpMessage,
                },
                getAppendNotificationSettingsField(integration),
            ];
        }

        case IntegrationType.DatadogStabilityMonitoring: {
            const provider = integrationProvider as IntegrationProviderDatadog;

            if (!provider) {
                return [];
            }

            return [
                {
                    fieldType: FieldType.Custom,
                    skipFormItemWrapper: true,
                    renderField: () => (
                        <DatadogStabilityMonitoringForm
                            integration={integration as IntegrationDatadogStabilityMonitoring}
                            provider={provider}
                            platform={platform}
                            appId={appId}
                        />
                    ),
                },
            ];
        }

        case IntegrationType.NewRelicStabilityMonitoring: {
            const provider = integrationProvider as IntegrationProviderNewRelic;

            if (!provider) {
                return [];
            }

            return [
                newRelicMobileApplicationFormField({
                    label: 'Production Application',
                    placeholder: 'Select',
                    fieldName: 'prodEnvironmentIdentifier',
                    applications: provider.data.mobileApplications ?? [],
                    helpMessage: "Select the New Relic application that you'd like to monitor for stability",
                    initialValue: integration.data.prodMobileApplication.applicationGuid ?? null,
                    allowClear: false,
                    required: true,
                }),
                newRelicMobileApplicationFormField({
                    label: 'Beta Application',
                    placeholder: 'Select',
                    fieldName: 'betaEnvironmentIdentifier',
                    applications: provider.data.mobileApplications ?? [],
                    helpMessage: 'If you have a Beta Testing integration connected, specify your beta environment',
                    initialValue: integration.data.betaMobileApplication?.applicationGuid ?? undefined,
                    allowClear: true,
                    required: false,
                }),
            ];
        }

        case IntegrationType.NewRelicAnalytics: {
            const provider = integrationProvider as IntegrationProviderNewRelic;

            if (!provider) {
                return [];
            }

            const baseEventsFormField = baseEventsToMonitorFormField(
                integration.id,
                integration.data?.selectedEventsAndParams,
            );

            return [
                newRelicMobileApplicationFormField({
                    label: 'Production Application',
                    placeholder: 'Select',
                    fieldName: 'appId',
                    applications: provider.data.mobileApplications ?? [],
                    helpMessage: "Select the New Relic application that you'd like to monitor",
                    initialValue: integration.data.application.applicationGuid ?? null,
                    required: true,
                    allowClear: false,
                }),
                {
                    ...baseEventsFormField,

                    renderField: () => {
                        return <EventsAndParamsInput integrationId={integration.id} eventsAndParams={undefined} />;
                    },
                },
            ];
        }

        case IntegrationType.Crowdin: {
            const provider = integrationProvider as IntegrationProviderCrowdin;
            if (!provider) {
                return [];
            }

            const projectId = integration.data?.selectedProject?.id;
            const metadataProjectId = integration.data?.selectedMetadataProject?.id;

            return [
                {
                    label: 'Project',
                    initialValue: projectId,
                    name: 'projectId',
                    fieldType: FieldType.Select,
                    data:
                        provider.data?.projects.map((project) => {
                            return {
                                value: project.id,
                                displayValue: project.name,
                            };
                        }) ?? [],
                    rules: [{ required: true }],
                },
                {
                    label: 'Metadata Project',
                    initialValue: metadataProjectId,
                    data:
                        provider.data?.projects.map((project) => {
                            return {
                                value: project.id,
                                displayValue: project.name,
                            };
                        }) ?? [],
                    name: 'metadataProjectId',
                    fieldType: FieldType.Select,
                    helpMessage:
                        'The project you use to track the translation of release metadata (e.g. release notes, keywords)',
                },
            ];
        }

        case IntegrationType.Lokalise: {
            const provider = integrationProvider as IntegrationProviderLokalise;
            if (!provider) {
                return [];
            }

            const projectId = integration.data?.selectedProject?.id;
            const metadataProjectId = integration.data?.selectedMetadataProject?.id;

            return [
                {
                    label: 'Project',
                    initialValue: projectId,
                    name: 'projectId',
                    fieldType: FieldType.Select,
                    data:
                        provider.data?.projects.map((project) => {
                            return {
                                value: project.id,
                                displayValue: project.name,
                            };
                        }) ?? [],
                    rules: [{ required: true }],
                },
                {
                    label: 'Metadata Project',
                    initialValue: metadataProjectId,
                    data:
                        provider.data?.projects.map((project) => {
                            return {
                                value: project.id,
                                displayValue: project.name,
                            };
                        }) ?? [],
                    name: 'metadataProjectId',
                    fieldType: FieldType.Select,
                    helpMessage:
                        'The project you use to track the translation of release metadata (e.g. release notes, keywords)',
                },
            ];
        }

        case IntegrationType.PagerDutyScheduling:
        case IntegrationType.RootlyScheduling:
        case IntegrationType.IncidentIoScheduling: {
            const provider = integrationProvider as
                | IntegrationProviderPagerDuty
                | IntegrationProviderRootly
                | IntegrationProviderIncidentIo;
            if (!provider) {
                return [];
            }
            const scheduleId = integration.data?.selectedSchedule?.id;

            return [
                {
                    label: 'Schedule',
                    initialValue: scheduleId,
                    data: provider.data?.schedules ?? [],
                    name: 'scheduleId',
                    fieldType: FieldType.Custom,

                    renderField: (data: IntegrationScheduleInfo[]) => {
                        return (
                            <Select showSearch filterOption={filterOptionLabel} placeholder="Select">
                                {data.map((schedule) => {
                                    return (
                                        <Select.Option value={schedule.id} key={schedule.id} label={schedule.name}>
                                            {schedule.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                },
            ];
        }
        case IntegrationType.OpsgenieScheduling: {
            const provider = integrationProvider as IntegrationProviderOpsgenie;
            if (!provider) {
                return [];
            }

            const initialRotationId = integration.data?.selectedRotation?.id;

            const rotationsForSchedule = provider.data?.rotations?.filter(
                (rotation) => rotation.scheduleId === opsgenieSchedule,
            );

            return [
                {
                    label: 'Schedule',
                    initialValue: opsgenieSchedule,
                    data: provider.data?.schedules ?? [],
                    name: 'scheduleId',
                    fieldType: FieldType.Custom,

                    renderField: (data: AtlassianScheduleInfo[]) => {
                        return (
                            <Select
                                showSearch
                                filterOption={filterOptionLabel}
                                placeholder="Select"
                                onChange={onChangeOpsgenieSchedule}
                            >
                                {data.map((schedule) => {
                                    return (
                                        <Select.Option value={schedule.id} key={schedule.id} label={schedule.name}>
                                            {schedule.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Rotation',
                    initialValue: initialRotationId,
                    data: rotationsForSchedule ?? [],
                    name: 'rotationId',
                    fieldType: FieldType.Custom,

                    renderField: (data: AtlassianRotationInfo[]) => {
                        return (
                            <Select showSearch filterOption={filterOptionLabel} placeholder="Select">
                                {data.map((rotation) => {
                                    return (
                                        <Select.Option value={rotation.id} key={rotation.id} label={rotation.name}>
                                            {rotation.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                },
            ];
        }

        case IntegrationType.JiraServiceManagementScheduling: {
            const provider = integrationProvider as IntegrationProviderJiraServiceManagement;
            if (!provider) {
                return [];
            }

            const initialRotationId = integration.data?.selectedRotation?.id;

            const rotationsForSchedule = provider.data?.availableRotations?.filter(
                (rotation) => rotation.scheduleId === jsmSchedule,
            );

            return [
                {
                    label: 'Schedule',
                    initialValue: jsmSchedule,
                    data: provider.data?.availableSchedules ?? [],
                    name: 'scheduleId',
                    fieldType: FieldType.Custom,

                    renderField: (data: AtlassianScheduleInfo[]) => {
                        return (
                            <Select
                                showSearch
                                filterOption={filterOptionLabel}
                                placeholder="Select"
                                onChange={onChangeJSMSchedule}
                            >
                                {data.map((schedule) => {
                                    return (
                                        <Select.Option value={schedule.id} key={schedule.id} label={schedule.name}>
                                            {schedule.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                },
                {
                    label: 'Rotation',
                    initialValue: initialRotationId,
                    data: rotationsForSchedule ?? [],
                    name: 'rotationId',
                    fieldType: FieldType.Custom,

                    renderField: (data: AtlassianRotationInfo[]) => {
                        return (
                            <Select showSearch filterOption={filterOptionLabel} placeholder="Select">
                                {data.map((rotation) => {
                                    return (
                                        <Select.Option value={rotation.id} key={rotation.id} label={rotation.name}>
                                            {rotation.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                },
            ];
        }

        case IntegrationType.DynatraceStabilityMonitoring: {
            const provider = integrationProvider as IntegrationProviderDynatrace;

            if (!provider) {
                return [];
            }

            return [
                {
                    label: 'Production RUM application',
                    initialValue: integration.data?.prodRumApp?.id,
                    name: 'prodEnvironmentIdentifier',
                    fieldType: FieldType.Custom,
                    placeholder: 'Select',
                    data: provider.data.rumApplications ?? [],
                    renderField: (apps: DynatraceRumApplication[]) => {
                        return (
                            <Select>
                                {apps.map((app) => {
                                    return (
                                        <Select.Option value={app.id} key={app.id} label={app.name}>
                                            <Space direction="horizontal">
                                                <Text>{app.name}</Text>

                                                <Text style={{ fontSize: 10, color: antGrey.grey6 }}>({app.id})</Text>
                                            </Space>
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    rules: [{ required: true }],
                },
                {
                    label: 'Beta RUM application',
                    initialValue: integration.data?.betaRumApp?.id,
                    name: 'betaEnvironmentIdentifier',
                    fieldType: FieldType.Custom,
                    placeholder: 'Select',
                    data: provider.data.rumApplications ?? [],
                    renderField: (apps: DynatraceRumApplication[]) => {
                        return (
                            <Select>
                                {apps.map((app) => {
                                    return (
                                        <Select.Option value={app.id} key={app.id} label={app.name}>
                                            <Space direction="horizontal">
                                                <Text>{app.name}</Text>

                                                <Text style={{ fontSize: 10, color: antGrey.grey6 }}>({app.id})</Text>
                                            </Space>
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    },
                    helpMessage: 'If you have a Beta Testing integration connected, specify your beta RUM application',
                    rules: [{ required: false }],
                },
            ];
        }
        case IntegrationType.LuciqStabilityMonitoring: {
            const luciqData = integration.data as LuciqStabilityMonitoringIntegrationData | undefined;
            const luciqUrlHelp =
                'Locate this value from your Luciq dashboard URL: https://dashboard.luciq.ai/applications/<app slug>/<environment>/…';
            return [
                {
                    label: 'App slug',
                    initialValue: luciqData?.appSlug,
                    name: 'luciqAppSlug',
                    fieldType: FieldType.Input,
                    helpMessage: luciqUrlHelp,
                    rules: [{ required: true }],
                },
                {
                    label: 'Environment',
                    initialValue: luciqData?.appMode,
                    name: 'luciqAppMode',
                    fieldType: FieldType.Input,
                    helpMessage: `E.g. "production" or "beta". ${luciqUrlHelp}`,
                    rules: [{ required: true }],
                },
                {
                    label: 'App token',
                    initialValue: luciqData?.appToken,
                    name: 'luciqAppToken',
                    fieldType: FieldType.Input,
                    helpMessage:
                        'With the above app and environment selected, go to Settings > SDK Integration. The app token can be found in the code snippet on that page.',
                    rules: [{ required: true }],
                },
            ];
        }
        case IntegrationType.LuciqAnalytics: {
            const provider = integrationProvider as IntegrationProviderLuciq;
            const luciqData = integration.data as LuciqAnalyticsIntegrationData | undefined;
            const luciqUrlHelp =
                'Locate this value from your Luciq dashboard URL: https://dashboard.luciq.ai/applications/<app slug>/<environment>/…';
            return [
                {
                    label: 'App slug',
                    initialValue: luciqData?.appSlug,
                    name: 'luciqAppSlug',
                    fieldType: FieldType.Input,
                    helpMessage: luciqUrlHelp,
                    rules: [{ required: true }],
                },
                {
                    label: 'Environment',
                    initialValue: luciqData?.appMode,
                    name: 'luciqAppMode',
                    fieldType: FieldType.Input,
                    helpMessage: `E.g. "production" or "beta". ${luciqUrlHelp}`,
                    rules: [{ required: true }],
                },
                {
                    label: 'App token',
                    initialValue: luciqData?.appToken,
                    name: 'luciqAppToken',
                    fieldType: FieldType.Input,
                    helpMessage:
                        'With the above app and environment selected, go to Settings > SDK Integration. The app token can be found in the code snippet on that page.',
                    rules: [{ required: true }],
                },
                {
                    label: 'Metrics',
                    initialValue: luciqData?.selectedEventsAndParams?.map((e) => e.eventName) ?? [],
                    name: 'rawSelectedMetrics',
                    fieldType: FieldType.Custom,
                    data: provider?.data?.availableMetrics ?? [],
                    renderField: (metrics: string[]) => (
                        <Select mode="multiple" placeholder="Select metrics">
                            {metrics.map((metric) => (
                                <Select.Option key={metric} value={metric}>
                                    {metric}
                                </Select.Option>
                            ))}
                        </Select>
                    ),
                    rules: [{ required: true, message: 'Please select at least one metric' }],
                },
            ];
        }
        case IntegrationType.CustomAnalytics: {
            return [];
        }
        case IntegrationType.GoogleCalendar: {
            const provider = integrationProvider as IntegrationProviderGoogle;
            if (!provider) {
                return [];
            }

            if (integration.data?.appDisplayName) {
                calendarIntegrationAppDisplayName = integration.data?.appDisplayName;
            }

            return [
                {
                    label: 'Calendar ID',
                    initialValue: integration.data?.calendarId,
                    name: 'calendarId',
                    fieldType: FieldType.Input,
                    rules: [{ required: true }],
                    helpMessage:
                        'You can find the calendar ID under Calendar settings > Integrate calendar > Calendar ID',
                },
                {
                    label: 'App name on calendar events',
                    initialValue: calendarIntegrationAppDisplayName,
                    name: 'appDisplayName',
                    fieldType: FieldType.Input,
                    rules: [{ required: true }],
                },
            ];
        }
        case IntegrationType.GooglePersonalCalendar: {
            const provider = integrationProvider as IntegrationProviderGoogleOAuth;
            if (!provider) {
                return [];
            }

            if (integration.data?.appDisplayName) {
                calendarIntegrationAppDisplayName = integration.data?.appDisplayName;
            }

            return [
                {
                    label: 'Calendar',
                    initialValue: integration.data?.calendarId,
                    data: provider.data?.availableCalendars ?? [],
                    name: 'calendarId',
                    fieldType: FieldType.Custom,
                    rules: [{ required: true }],

                    renderField: (calendars) => {
                        return (
                            <Select
                                showSearch
                                placeholder="Select a calendar"
                                optionFilterProp="children"
                                style={{ width: '100%' }}
                            >
                                {calendars?.map((calendar) => (
                                    <Select.Option key={calendar.id} value={calendar.id}>
                                        {calendar.summary}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    },
                    helpMessage: 'Select the calendar you want to use for release events',
                },
                {
                    label: 'App name on calendar events',
                    initialValue: calendarIntegrationAppDisplayName,
                    name: 'appDisplayName',
                    fieldType: FieldType.Input,
                    rules: [{ required: true }],
                },
            ];
        }
        default:
            return [];
    }
};

const eventsToMonitorHelperText: (integrationType: IntegrationType) => string = (integrationType) => {
    const [, propertyPluralName] = eventPropertyTerminology(integrationType);
    return `You can optionally add associated ${propertyPluralName} for events to monitor their values.`;
};

interface NewRelicMobileApplicationFormFieldProps {
    label: string;
    placeholder: string;
    fieldName: string;
    applications: NewRelicMobileApplication[];
    helpMessage?: string;
    initialValue?: string;
    allowClear?: boolean;
    required: boolean;
}

const ExpoEASChannelSelect = ({
    channelsByProjectId,
    value,
    onChange,
}: {
    channelsByProjectId: Record<string, ExpoEASChannel[]>;
    value?: string;
    onChange?: (value: string) => void;
}) => {
    const form = Form.useFormInstance();
    const projectId = Form.useWatch('projectId', form);
    const channels = projectId ? (channelsByProjectId[projectId] ?? []) : [];
    return (
        <Select value={value} onChange={onChange} showSearch filterOption={filterOptionLabel} disabled={!projectId}>
            {channels.map((channel) => (
                <Select.Option value={channel.name} key={channel.id} label={channel.name}>
                    {channel.name}
                </Select.Option>
            ))}
        </Select>
    );
};

const newRelicMobileApplicationFormField = ({
    label,
    placeholder,
    helpMessage,
    applications,
    fieldName,
    initialValue,
    allowClear,
    required,
}: NewRelicMobileApplicationFormFieldProps) => {
    return {
        label: label,
        initialValue: initialValue,
        name: fieldName,
        fieldType: FieldType.Custom,
        data: applications ?? [],
        helpMessage: helpMessage,
        rules: [{ required: required }],
        renderField: (data: NewRelicMobileApplication[]) => {
            return (
                <Select allowClear={allowClear} placeholder={placeholder}>
                    {data.map((app) => {
                        return (
                            <Select.Option value={app.applicationGuid} key={app.applicationGuid} label={app.name}>
                                <Text>{app.name}</Text>
                                <Text>&nbsp;&nbsp;&nbsp;</Text>
                                <Text style={{ fontSize: 10, color: antGrey.grey6 }}>({app.appId.toString()})</Text>
                            </Select.Option>
                        );
                    })}
                </Select>
            );
        },
    };
};

interface DatadogObservabilityAndAnalyticsFormProps {
    integration: IntegrationDatadogAnalytics;
    provider: IntegrationProviderDatadog;
    platform: AppPlatform;
    appId: string;
}

const datadogTabContentStyle: React.CSSProperties = {
    background: runwayColors.white,
    borderRadius: '0 0 8px 8px',
    padding: 16,
    border: `1px solid ${antGrey.grey4}`,
    borderTop: 'none',
};

const datadogCustomMetricAggregationOptions = [
    { label: 'Average', value: DatadogMetricQueryAggregation.Average },
    { label: 'Count', value: DatadogMetricQueryAggregation.Count },
    { label: 'Sum', value: DatadogMetricQueryAggregation.Sum },
    { label: 'Minimum', value: DatadogMetricQueryAggregation.Minimum },
    { label: 'Maximum', value: DatadogMetricQueryAggregation.Maximum },
];

const DatadogObservabilityAndAnalyticsForm: React.FC<DatadogObservabilityAndAnalyticsFormProps> = ({
    integration,
    provider,
    platform,
    appId,
}) => {
    const data = integration.data;
    const form = Form.useFormInstance();
    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));
    const defaultProdServiceValue: string = (appStoreIntegration?.data as { bundleId?: string })?.bundleId ?? '';
    const rumApplications =
        provider.data.rumApplications?.filter(isValidDatadogRumApplicationTypeForAppPlatform(platform)) ?? [];

    return (
        <>
            <Form.Item
                hidden
                name="datadogObaaSourceSelection"
                dependencies={['rawSelectedEventsAndParams', 'customMetrics']}
                rules={[
                    {
                        validator: () => {
                            const hasRumEvents = (form.getFieldValue('rawSelectedEventsAndParams') ?? []).length > 0;
                            const hasCustomMetrics = (form.getFieldValue('customMetrics') ?? []).length > 0;
                            return hasRumEvents || hasCustomMetrics ?
                                    Promise.resolve()
                                :   Promise.reject(new Error('Configure at least one RUM event or custom metric.'));
                        },
                    },
                ]}
            >
                <Input type="hidden" />
            </Form.Item>
            <Tabs destroyInactiveTabPane={false} type="card" tabBarStyle={{ marginBottom: 0 }}>
                <Tabs.TabPane tab="RUM" key="rum">
                    <div style={datadogTabContentStyle}>
                        <Form.Item
                            label="RUM Application"
                            name="rumApplicationId"
                            initialValue={data.rumApplicationId ?? null}
                            dependencies={['rawSelectedEventsAndParams']}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        const hasRumEvents =
                                            (form.getFieldValue('rawSelectedEventsAndParams') ?? []).length > 0;
                                        return !hasRumEvents || value ?
                                                Promise.resolve()
                                            :   Promise.reject(
                                                    new Error(
                                                        'A RUM application is required when RUM events are configured',
                                                    ),
                                                );
                                    },
                                },
                            ]}
                        >
                            <Select allowClear>
                                {rumApplications.map((app) => (
                                    <Select.Option value={app.applicationId} key={app.applicationId} label={app.name}>
                                        <Text>{datadogRumApplicationDisplayableString(app)}</Text>
                                        <Text>&nbsp;&nbsp;&nbsp;</Text>
                                        <Text style={{ fontSize: 10, color: antGrey.grey6 }}>
                                            ({app.applicationId})
                                        </Text>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="Events to monitor"
                            name="rawSelectedEventsAndParams"
                            initialValue={data.selectedEventsAndParams ?? []}
                        >
                            <EventsAndParamsInput integrationId={integration.id} eventsAndParams={undefined} />
                        </Form.Item>
                        <Form.Item
                            name="shouldFilterByBundleId"
                            valuePropName="checked"
                            initialValue={!!data.shouldFilterByBundleId}
                            help="Filter RUM data by the bundle ID configured in the App Store integration"
                        >
                            <Checkbox>Filter by Bundle ID</Checkbox>
                        </Form.Item>
                    </div>
                </Tabs.TabPane>
                <Tabs.TabPane tab="Custom Metrics" key="custom-metrics">
                    <div style={datadogTabContentStyle}>
                        <Form.List
                            name="customMetrics"
                            initialValue={data.customMetrics ?? []}
                            rules={[
                                {
                                    validator: async (_, metrics: DatadogCustomMetric[]) => {
                                        const keys = (metrics ?? [])
                                            .filter((metric) => metric?.name?.trim() && metric?.aggregation)
                                            .map((metric) =>
                                                datadogCustomMetricKey({
                                                    ...metric,
                                                    name: metric.name.trim(),
                                                }),
                                            );
                                        if (new Set(keys).size !== keys.length) {
                                            throw new Error('Custom metric name and aggregation must be unique');
                                        }
                                    },
                                },
                            ]}
                        >
                            {(metricFields, { add, remove }, { errors }) => (
                                <Form.Item label="Custom metrics">
                                    {metricFields.map((field) => (
                                        <div
                                            key={field.key}
                                            style={{
                                                display: 'grid',
                                                gap: 8,
                                                gridTemplateColumns: 'minmax(0, 2fr) minmax(120px, 1fr) auto',
                                                alignItems: 'baseline',
                                            }}
                                        >
                                            <Form.Item
                                                name={[field.name, 'name']}
                                                rules={[{ required: true, message: 'Metric name is required' }]}
                                            >
                                                <Input placeholder="Metric name" />
                                            </Form.Item>
                                            <Form.Item
                                                name={[field.name, 'aggregation']}
                                                rules={[{ required: true, message: 'Aggregation is required' }]}
                                            >
                                                <Select options={datadogCustomMetricAggregationOptions} />
                                            </Form.Item>
                                            <Button
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(field.name)}
                                                aria-label="Remove custom metric"
                                            />
                                        </div>
                                    ))}
                                    <Form.ErrorList errors={errors} />
                                    <Button
                                        type="dashed"
                                        onClick={() =>
                                            add({
                                                aggregation: DatadogMetricQueryAggregation.Average,
                                                name: '',
                                            })
                                        }
                                        icon={<PlusOutlined />}
                                    >
                                        Add custom metric
                                    </Button>
                                </Form.Item>
                            )}
                        </Form.List>
                        <Form.Item
                            name="datadogSingleVersionPerFetch"
                            valuePropName="checked"
                            initialValue={!!data.singleVersionPerFetch}
                            help="Use this when Datadog rejects or truncates queries that return too many versions"
                        >
                            <Checkbox>Fetch one version per request</Checkbox>
                        </Form.Item>
                        <Form.List name="datadogMetricTagFilters" initialValue={data.metricTagFilters ?? []}>
                            {(filterFields, { add, remove }) => (
                                <Form.Item label="Additional metric tag filters">
                                    {filterFields.map((field) => (
                                        <div
                                            key={field.key}
                                            style={{ display: 'flex', alignItems: 'baseline', marginBottom: 8 }}
                                        >
                                            <Form.Item
                                                name={[field.name, 'key']}
                                                rules={[{ required: true, message: 'Tag key is required' }]}
                                            >
                                                <Input placeholder="Key" />
                                            </Form.Item>
                                            <Form.Item
                                                name={[field.name, 'value']}
                                                rules={[{ required: true, message: 'Tag value is required' }]}
                                            >
                                                <Input placeholder="Value" />
                                            </Form.Item>
                                            <Button
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(field.name)}
                                                aria-label="Remove metric tag filter"
                                            />
                                        </div>
                                    ))}
                                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                                        Add tag filter
                                    </Button>
                                </Form.Item>
                            )}
                        </Form.List>
                        <Form.Item
                            name="skipCustomMetricsFilterByService"
                            valuePropName="checked"
                            initialValue={!!data.skipCustomMetricsFilterByService}
                            help="Use this when custom metrics do not expose a service tag. Add an app-specific tag filter when the metric name is shared across apps."
                        >
                            <Checkbox>Skip filtering custom metrics by service</Checkbox>
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                if (
                                    !(getFieldValue('customMetrics') ?? []).length ||
                                    getFieldValue('skipCustomMetricsFilterByService')
                                ) {
                                    return null;
                                }
                                return (
                                    <>
                                        <Form.Item
                                            name="useDatadogServiceMapper"
                                            valuePropName="checked"
                                            initialValue={!!data.serviceMapper?.prodServiceValue}
                                            help={
                                                defaultProdServiceValue ?
                                                    `Use this when Datadog service values do not match the App Store bundle ID. By default Runway filters by service ${defaultProdServiceValue}`
                                                :   'Use this when Datadog service values do not match the App Store bundle ID'
                                            }
                                        >
                                            <Checkbox>Use custom Datadog service value</Checkbox>
                                        </Form.Item>
                                        <Form.Item noStyle shouldUpdate>
                                            {({ getFieldValue: getNestedFieldValue }) => {
                                                if (!getNestedFieldValue('useDatadogServiceMapper')) {
                                                    return null;
                                                }
                                                return (
                                                    <Form.Item
                                                        label="Production service value"
                                                        name="datadogProdServiceValue"
                                                        initialValue={
                                                            data.serviceMapper?.prodServiceValue ||
                                                            defaultProdServiceValue
                                                        }
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: 'Production service value is required',
                                                            },
                                                        ]}
                                                    >
                                                        <Input placeholder="Datadog production service value" />
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>
                                    </>
                                );
                            }}
                        </Form.Item>
                    </div>
                </Tabs.TabPane>
            </Tabs>
        </>
    );
};

interface DatadogStabilityMonitoringFormProps {
    integration: IntegrationDatadogStabilityMonitoring;
    provider: IntegrationProviderDatadog;
    platform: AppPlatform;
    appId: string;
}

const DatadogStabilityMonitoringForm: React.FC<DatadogStabilityMonitoringFormProps> = ({
    integration,
    provider,
    platform,
    appId,
}) => {
    const data = integration.data;
    const form = Form.useFormInstance();
    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));
    const betaTestingIntegration = useSelector((state: RootState) => selectBetaTestingIntegration(state, appId));
    const defaultProdServiceValue: string = (appStoreIntegration?.data as { bundleId?: string })?.bundleId ?? '';
    const defaultBetaServiceValue: string = (betaTestingIntegration?.data as { bundleId?: string })?.bundleId ?? '';
    const rumApplications =
        provider.data.rumApplications?.filter(isValidDatadogRumApplicationTypeForAppPlatform(platform)) ?? [];
    const tabContentStyle: React.CSSProperties = {
        background: runwayColors.white,
        borderRadius: '0 0 8px 8px',
        padding: 16,
        border: `1px solid ${antGrey.grey4}`,
        borderTop: 'none',
    };

    return (
        <Tabs destroyInactiveTabPane={false} type="card" tabBarStyle={{ marginBottom: 0 }}>
            <Tabs.TabPane tab="RUM" key="rum">
                <div style={tabContentStyle}>
                    <Form.Item
                        label="RUM Application"
                        name="rumApplicationId"
                        initialValue={data.rumApplicationId ?? null}
                        rules={[{ required: true }]}
                    >
                        <Select>
                            {rumApplications.map((app) => (
                                <Select.Option value={app.applicationId} key={app.applicationId} label={app.name}>
                                    <Text>{datadogRumApplicationDisplayableString(app)}</Text>
                                    <Text>&nbsp;&nbsp;&nbsp;</Text>
                                    <Text style={{ fontSize: 10, color: antGrey.grey6 }}>({app.applicationId})</Text>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="Production environment"
                        name="prodEnvironmentIdentifier"
                        initialValue={data.prodEnvironment ?? ''}
                        help="Environment values are listed in the Datadog Error Tracking dashboard: RUM > Error Tracking > Facets > CORE > Env"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Beta environment"
                        name="betaEnvironmentIdentifier"
                        initialValue={data.betaEnvironment}
                        help="If you have a Beta Testing integration connected, specify your beta environment"
                    >
                        <Input />
                    </Form.Item>
                    {data.shouldUseRUMForStabilityInfos && (
                        <Form.Item
                            name="shouldFilterByBundleId"
                            valuePropName="checked"
                            initialValue={!!data.shouldFilterByBundleId}
                            help="Fetch data for the bundle ID configured in the App Store or Beta Testing integrations"
                        >
                            <Checkbox>Filter by Bundle IDs</Checkbox>
                        </Form.Item>
                    )}
                    <Form.Item
                        label="Service facet"
                        name="datadogBundleFacet"
                        initialValue={data.bundleFacet}
                        help="Set a different value only if you have set up a custom facet to send the app's bundle ID to a property other than 'service'"
                        rules={[{ required: true, message: 'Service facet is required' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            onClick={() =>
                                form.setFieldsValue({
                                    datadogBundleFacet: data.defaultBundleFacet,
                                })
                            }
                        >
                            Reset service facet to default
                        </Button>
                    </Form.Item>
                </div>
            </Tabs.TabPane>
            {!data.shouldUseRUMForStabilityInfos && (
                <Tabs.TabPane tab="Custom Metrics" key="custom-metrics">
                    <div style={tabContentStyle}>
                        <Form.Item
                            name="datadogSingleVersionPerFetch"
                            valuePropName="checked"
                            initialValue={!!data.singleVersionPerFetch}
                            help="Use this when Datadog rejects or truncates queries that return too many versions"
                        >
                            <Checkbox>Fetch one version per request</Checkbox>
                        </Form.Item>
                        <Form.List name="datadogMetricTagFilters" initialValue={data.metricTagFilters ?? []}>
                            {(filterFields, { add, remove }) => (
                                <Form.Item label="Additional metric tag filters">
                                    {filterFields.map((field) => (
                                        <div
                                            key={field.key}
                                            style={{ display: 'flex', alignItems: 'baseline', marginBottom: 8 }}
                                        >
                                            <Form.Item
                                                name={[field.name, 'key']}
                                                rules={[{ required: true, message: 'Tag key is required' }]}
                                            >
                                                <Input placeholder="Key" />
                                            </Form.Item>
                                            <Form.Item
                                                name={[field.name, 'value']}
                                                rules={[{ required: true, message: 'Tag value is required' }]}
                                            >
                                                <Input placeholder="Value" />
                                            </Form.Item>
                                            <Button
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(field.name)}
                                                aria-label="Remove metric tag filter"
                                            />
                                        </div>
                                    ))}
                                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                                        Add tag filter
                                    </Button>
                                </Form.Item>
                            )}
                        </Form.List>
                        <Form.Item
                            name="useDatadogServiceMapper"
                            valuePropName="checked"
                            initialValue={!!data.serviceMapper?.prodServiceValue}
                            help={
                                defaultProdServiceValue ?
                                    `Use this when Datadog service values do not match the App Store or Beta Testing bundle IDs. By default Runway filters by service ${defaultProdServiceValue}${defaultBetaServiceValue ? ` (prod) and ${defaultBetaServiceValue} (beta)` : ''}`
                                :   'Use this when Datadog service values do not match the App Store or Beta Testing bundle IDs'
                            }
                        >
                            <Checkbox>Use custom Datadog service values</Checkbox>
                        </Form.Item>
                        <Form.Item shouldUpdate noStyle>
                            {(form) => {
                                if (!form.getFieldValue('useDatadogServiceMapper')) {
                                    return null;
                                }

                                return (
                                    <>
                                        <Form.Item
                                            label="Production service value"
                                            name="datadogProdServiceValue"
                                            initialValue={
                                                data.serviceMapper?.prodServiceValue || defaultProdServiceValue
                                            }
                                            rules={[
                                                { required: true, message: 'Production service value is required' },
                                            ]}
                                        >
                                            <Input placeholder="Datadog production service value" />
                                        </Form.Item>
                                        <Form.Item
                                            label="Beta service value"
                                            name="datadogBetaServiceValue"
                                            initialValue={
                                                data.serviceMapper?.betaServiceValue ??
                                                (defaultBetaServiceValue || null)
                                            }
                                        >
                                            <Input placeholder="Datadog beta service value" />
                                        </Form.Item>
                                        {(defaultProdServiceValue || defaultBetaServiceValue) && (
                                            <Form.Item>
                                                <Button
                                                    onClick={() =>
                                                        form.setFieldsValue({
                                                            datadogProdServiceValue: defaultProdServiceValue,
                                                            datadogBetaServiceValue: defaultBetaServiceValue || null,
                                                        })
                                                    }
                                                >
                                                    Reset to default values
                                                </Button>
                                            </Form.Item>
                                        )}
                                    </>
                                );
                            }}
                        </Form.Item>
                    </div>
                </Tabs.TabPane>
            )}
        </Tabs>
    );
};

interface EventsAndParamsInputProps {
    integrationId: IntegrationType;
    value?: EventAndParam[];
    onChange?: (value: EventAndParam[]) => void;
    eventsAndParams?: Map<string, AnalyticsProperty[]>;
}

const EventsAndParamsInput: React.FC<EventsAndParamsInputProps> = (props) => {
    const allEventsAndParams = [...(props.value ?? [])];
    const [selectedEvent, setSelectedEvent] = React.useState<string | null>(null);
    const [selectedParam, setSelectedParam] = React.useState<string | null>(null);

    const [propertySingular] = eventPropertyTerminology(props.integrationId, true);

    const onFinish = () => {
        if (!selectedEvent) {
            // This shouldn't happen
            return;
        }

        const eventAndParam: EventAndParam = {
            eventName: selectedEvent,
            paramName: selectedParam ?? undefined,
        };

        const existingEventAndParam = (props.value ?? []).find(
            (ep) => ep.eventName === selectedEvent && ep.paramName === selectedParam,
        );

        if (!existingEventAndParam) {
            const newEventsAndParams = allEventsAndParams.concat(eventAndParam);
            props.onChange?.(newEventsAndParams);
        }

        setSelectedEvent(null);
        setSelectedParam(null);
    };

    const removeEventAndParamFromList = (ep) => {
        remove(allEventsAndParams, (candidate) => candidate === ep);
        props.onChange?.(allEventsAndParams);
    };

    const eventAutoCompleteOptions = [...(props.eventsAndParams?.keys() ?? [])].map((event) => {
        return {
            value: event,
        };
    });

    const paramsForEvent = selectedEvent ? (props.eventsAndParams?.get(selectedEvent) ?? []) : [];
    const paramAutoCompleteOptions = paramsForEvent.map((param) => {
        return {
            label: param.name,
            value: param.id,
        };
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
            <Space direction="vertical">
                <Text className="ant-form-item-required" style={{ color: antGrey.grey8 }}>
                    Event
                </Text>
                <AutoComplete
                    style={{ width: '100%' }}
                    options={eventAutoCompleteOptions}
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e)}
                    filterOption={(inputValue, option) =>
                        option?.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    placeholder="Event name"
                />
                {upperHelperMessageForIntegration(props.integrationId)}
            </Space>
            <Space direction="vertical">
                <Text style={{ color: antGrey.grey8 }}>
                    {propertySingular}
                    {' (optional)'}
                    <Tooltip title={eventsToMonitorHelperText(props.integrationId)}>
                        <InfoCircleOutlined style={{ color: antGrey.grey7, marginLeft: 8 }} />
                    </Tooltip>
                </Text>
                <AutoComplete
                    style={{ width: '100%' }}
                    options={paramAutoCompleteOptions}
                    value={selectedParam}
                    onChange={(p) => setSelectedParam(p)}
                    filterOption={(inputValue, option) =>
                        option?.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1 ||
                        option?.label?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    placeholder={propertyPlaceholderForIntegration(props.integrationId, propertySingular)}
                />
                {lowerHelperMessageForIntegration(props.integrationId)}
            </Space>
            <Button
                type="primary"
                style={{ width: '100px' }}
                onClick={onFinish}
                disabled={!selectedEvent}
                icon={<PlusOutlined />}
                size="small"
            >
                Add
            </Button>
            <Space wrap style={{ marginTop: 18 }}>
                {allEventsAndParams.map((ep) => {
                    return (
                        <Tag
                            key={ep.eventName + ep.paramName}
                            closable
                            onClose={() => removeEventAndParamFromList(ep)}
                            color={colorWithAlpha(rwColors.primary, 0.8)}
                        >
                            {ep.paramName ? `${ep.eventName} - ${ep.paramName}` : ep.eventName}
                        </Tag>
                    );
                })}
            </Space>
        </div>
    );
};

const propertyPlaceholderForIntegration = (integrationId: IntegrationType, propertySingular: string) => {
    switch (integrationId) {
        case IntegrationType.GoogleAnalytics:
            return `E.g. amount`;
        case IntegrationType.DatadogAnalytics:
            return `E.g. context.amount`;
        default:
            return `${propertySingular} name`;
    }
};

const upperHelperMessageForIntegration = (integrationId: IntegrationType) => {
    switch (integrationId) {
        case IntegrationType.NewRelicAnalytics:
            return (
                <Text type="secondary">
                    For custom events, enter event type and name delimited with '.' in the Event field. For example
                    'Action.ClickButton'.
                </Text>
            );
        default:
            return null;
    }
};

const lowerHelperMessageForIntegration = (integrationId: IntegrationType) => {
    switch (integrationId) {
        case IntegrationType.GoogleAnalytics:
            return (
                <Text type="secondary">
                    For more information on how to specify Google Analytics metrics, visit our{' '}
                    <Link
                        href="https://docs.runway.team/integrations/observability-and-analytics/google-analytics#configure-the-google-analytics-integration"
                        target="_blank"
                    >
                        docs
                    </Link>
                    .
                </Text>
            );
        case IntegrationType.DatadogAnalytics:
            return (
                <Text type="secondary">
                    For more information on how to specify Datadog metrics, visit our{' '}
                    <Link
                        href="https://docs.runway.team/integrations/observability-and-analytics/datadog#configure-the-datadog-integration"
                        target="_blank"
                    >
                        docs
                    </Link>
                    .
                </Text>
            );
        default:
            return null;
    }
};

export const getFormFieldForEmbraceIntegrationForm = (
    apps: { value: string; label: string }[],
    label: string,
    name: string,
    isRequired: boolean,
    initialValue?: EmbraceApp,
): FormFieldData => {
    return {
        label: label,
        data: apps,
        initialValue: initialValue ?? null,
        name: name,
        fieldType: FieldType.Custom,
        rules: [{ required: isRequired }],
        renderField: (data) => {
            return <Select options={data} allowClear placeholder="Select" />;
        },
    };
};

export const workflowArgumentFieldForWorkflowType = (
    workflowArguments: Record<WorkflowType, Record<string, string>> | null,
    workflowType?: WorkflowType,
): FormFieldData => {
    let argumentPairs: Record<string, string> = {};
    if (workflowArguments && workflowType !== undefined) {
        argumentPairs = workflowArguments[workflowType];
    }
    return workflowArgumentField(argumentPairs, workflowType, null);
};

export const workflowArgumentField = (
    workflowArguments?: Record<string, string>,
    workflowType?: WorkflowType,
    fieldName?: string & NamePath,
): FormFieldData => {
    let argumentsForWorkflow: WorkflowArgument[] = [];
    if (workflowArguments) {
        argumentsForWorkflow = [];
        for (const [key, value] of Object.entries(workflowArguments)) {
            argumentsForWorkflow.push({
                argumentKey: key,
                argumentValue: value,
            });
        }
    }
    const name =
        workflowType ? `${workflowType}WorkflowArguments`
        : fieldName ? fieldName
        : 'workflowArguments';
    return {
        label: null,
        name: name,
        fieldType: FieldType.List,
        showDivider: false,
        initialValue: argumentsForWorkflow,
        nestedListFields: [
            {
                fieldType: FieldType.Custom,
                style: { margin: 0 },
                renderField: ({ index, listOperations: { remove }, listFieldData }) => {
                    return (
                        <Form.Item
                            shouldUpdate
                            noStyle
                            style={{
                                width: '90%',
                            }}
                        >
                            {() => {
                                return (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: '10px',
                                        }}
                                    >
                                        <Form.Item
                                            noStyle
                                            {...listFieldData}
                                            rules={[
                                                {
                                                    required: true,
                                                    type: 'string',
                                                },
                                            ]}
                                            name={[listFieldData.name, 'argumentKey']}
                                            key={`argumentKey${index}`}
                                        >
                                            <Input placeholder="Key" />
                                        </Form.Item>
                                        <Form.Item
                                            noStyle
                                            {...listFieldData}
                                            rules={[
                                                {
                                                    required: true,
                                                    type: 'string',
                                                },
                                            ]}
                                            name={[listFieldData.name, 'argumentValue']}
                                            key={`argumentValue${index}`}
                                        >
                                            <Input placeholder="Value" />
                                        </Form.Item>
                                        <Button
                                            icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                            danger
                                            type="link"
                                            onClick={() => remove(index)}
                                        />
                                    </div>
                                );
                            }}
                        </Form.Item>
                    );
                },
            },
        ],
        listChildrenWrapper: (children, { add }) => {
            const hasArguments = (children as React.ReactNode[]).length > 0;
            const buttonText = hasArguments ? 'Add another workflow argument' : 'Add workflow argument';
            return (
                <Space
                    direction="vertical"
                    style={{
                        width: '100%',
                        borderRadius: 8,
                        paddingBottom: '16px',
                    }}
                    size={16}
                >
                    {children}
                    {hasArguments && (
                        <Text type="secondary">
                            Tokenized patterns are supported.
                            <Popover
                                content={
                                    <PatternTokenHelperTextComponent availableTokens={BuildTriggerAvailableTokens} />
                                }
                            >
                                <InfoCircleOutlined style={{ color: antGrey.grey6, marginInlineStart: 8 }} />
                            </Popover>
                        </Text>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 'fit-content' }}>
                        <Button
                            icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                            onClick={() => add()}
                            size="small"
                            style={{ display: 'inline-flex', alignItems: 'center' }}
                        >
                            {buttonText}
                        </Button>
                        <Tooltip title="Workflow arguments are passed when you run your workflow from Runway, and are also used for filtering workflow runs.">
                            <InfoCircleOutlined style={{ color: antGrey.grey6, display: 'flex' }} />
                        </Tooltip>
                    </div>
                </Space>
            );
        },
    } as FormFieldData;
};

// Tokens supported when triggering a build — shared by workflow arguments and the Custom CI
// trigger body template so both accept the same set. Kept in sync with the backend allow-list
// CustomCITriggerAllowedTokens in lib/runway/integration_custom_ci.go.
const BuildTriggerAvailableTokens = [
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.VersionMajorMinor,
    PatternToken.VersionMajor,
    PatternToken.ReleaseName,
    PatternToken.ReleaseType,
    PatternToken.ReleasePilot,
    PatternToken.ReleaseBranchName,
    PatternToken.WorkingBranchName,
    PatternToken.BuildCI,
    PatternToken.CommitShortHash,
    PatternToken.CommitFullHash,
    PatternToken.AppName,
    PatternToken.AppPlatform,
    PatternToken.StepConfigId,
];

// Generalized type for UI
export interface FirebaseApp {
    appId: string;
    displayName: string;
    bundleOrPackageName: string;
}

export const firebaseAppFromAndroidApp = (androidApp: FirebaseAndroidApp) => {
    return {
        appId: androidApp.appId,
        displayName: androidApp.displayName,
        bundleOrPackageName: androidApp.packageName,
    };
};

export const firebaseAppFromIosApp = (iosApp: FirebaseIosApp) => {
    return {
        appId: iosApp.appId,
        displayName: iosApp.displayName,
        bundleOrPackageName: iosApp.bundleId,
    };
};

export const labelForASCWorkflow: (workflow: ASCWorkflow) => string = (workflow) => {
    // annotate label vs disabling selection because deactivated workflows can still be run manually
    return workflow.name + (!workflow.isEnabled ? ' (deactivated)' : '');
};

const baseEventsToMonitorFormField: (
    integrationType: IntegrationType,
    initialValue?: EventAndParam[],
) => FormFieldData = (integrationType, initialValue) => {
    return {
        label: 'Events to monitor',
        initialValue: initialValue ?? [],
        name: 'rawSelectedEventsAndParams',
        fieldType: FieldType.Custom,
        rules: [{ min: 1, type: 'array', message: 'Please select at least one event to monitor' }],
    };
};

const primaryChannelsHelpMessage = (
    <Space direction="vertical" size={'small'}>
        <Text type="secondary">Select or enter one or more channel names.</Text>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
                <Text type="secondary" style={{ paddingBottom: 4 }}>
                    Pattern token strings are supported{' '}
                    <Popover
                        content={<PatternTokenHelperTextComponent availableTokens={CommsChannelAvailableTokens} />}
                    >
                        <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                    </Popover>{' '}
                    <Tooltip title="If Runway cannot resolve selected tokens (e.g., no release name is set), the channel will not be created. Changing the release name or version during a release may disrupt notification delivery.">
                        <WarningFilled style={{ paddingLeft: 6, color: runwayColors.statusYellow }} />
                    </Tooltip>
                </Text>
            </li>
            <li>
                <Text type="secondary">
                    All notifications will go to your primary channel(s), unless an override is defined in an individual
                    notification&apos;s settings.
                </Text>
            </li>
        </ul>
    </Space>
);

const getAppendNotificationSettingsField = (integration: IntegrationMicrosoftTeams | IntegrationSlack) => ({
    data: 'Include a link to manage settings on all notifications',
    name: 'shouldAppendNotificationSettings',
    // For this case we consider the default to be true to avoid all existing integrations changing behavior
    initialValue: integration?.data?.shouldAppendNotificationSettings ?? true,
    fieldType: FieldType.Checkbox,
    rules: [{ required: false }],
});

type StatsigFieldProps = {
    value?: any;
    onChange?: (value: any) => void;
    availableTags?: string[];
};

const StatsigPlatformFilterMatchTypeField = ({ value, onChange }: StatsigFieldProps): JSX.Element => {
    const form = Form.useFormInstance();
    const source = Form.useWatch('featureFlagPlatformFilterSource', form) ?? 'none';
    if (source === 'none') {
        return <></>;
    }
    const options = source === 'rules' ? STATSIG_RULES_MATCH_TYPE_OPTIONS : STATSIG_PLATFORM_FILTER_MATCH_TYPE_OPTIONS;
    return <Select value={value} onChange={onChange} options={options} />;
};

const StatsigPlatformFilterValueField = ({ value, onChange }: StatsigFieldProps): JSX.Element => {
    const form = Form.useFormInstance();
    const source = Form.useWatch('featureFlagPlatformFilterSource', form) ?? 'none';
    if (source === 'none') {
        return <></>;
    }
    if (source === 'rules') {
        return (
            <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select operating system(s)"
                allowClear
                showSearch
                filterOption={filterOptionLabel}
                value={value}
                onChange={onChange}
            >
                {FEATURE_FLAG_PLATFORM_FILTER_SUGGESTIONS.map((os) => (
                    <Select.Option key={os} value={os} label={os}>
                        {os}
                    </Select.Option>
                ))}
            </Select>
        );
    }
    return <Input placeholder="Filter text" allowClear value={value} onChange={(e) => onChange?.(e.target.value)} />;
};

const StatsigPlatformFilterSourceField = ({ value, onChange }: StatsigFieldProps): JSX.Element => {
    const form = Form.useFormInstance();
    return (
        <Select
            value={value}
            options={STATSIG_PLATFORM_FILTER_SOURCE_OPTIONS}
            onChange={(nextSource: string) => {
                onChange?.(nextSource);
                if (nextSource === 'none') {
                    form.setFieldsValue({
                        featureFlagPlatformFilterMatchType: null,
                        featureFlagPlatformFilter: null,
                    });
                    return;
                }
                if (nextSource === 'rules') {
                    form.setFieldsValue({
                        featureFlagPlatformFilterMatchType: 'equals',
                        featureFlagPlatformFilter: [],
                    });
                    return;
                }
                form.setFieldsValue({
                    featureFlagPlatformFilterMatchType: 'contains',
                    featureFlagPlatformFilter: null,
                });
            }}
        />
    );
};
